import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, InfoWindow, Polygon } from '@react-google-maps/api';
import AdvancedMarker from '../common/AdvancedMarker';
import {
  Badge,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Collapse,
  Tooltip
} from '@mui/material';
import {
  Person as DriversIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Map as MapIcon,
  ChevronRight as ChevronRightIcon,
  Route as RouteIcon,
  Event as EventIcon,
  FlashOn as FlashOnIcon,
  Phone as PhoneIcon,
  Smartphone as SmartphoneIcon,
  LocalHospital as MedicalIcon,
  Repeat as RecurringIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Cancel as CancelledIcon,
  PlayArrow as StartIcon,
  Stop as EndIcon,
  SwapHoriz as SwapHorizIcon,
  LocalTaxi as TaxiIcon,
  ContentCopy as ContentCopyIcon,
  Add as AddIcon,
  StickyNote2 as NoteIcon,
  Star as StarIcon,
  AccessTime as AccessTimeIcon,
  LocationOn as LocationOnIcon,
  Flag as FlagIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import LocationService from '../../utils/locationService';
import PaymentChip from '../common/PaymentChip';
import { fetchSettings } from '../../redux/slices/settingsSlice';
import { fetchSectors } from '../../redux/slices/sectorsSlice';
import { isOperatorRole } from '../../redux/slices/authSlice';
import { updateRideStatus } from '../../redux/slices/ridesSlice';
import {
  getLiveMapRelevantRides,
  toMs,
} from '../../utils/rideDashboardFilters';
import { buildDispatcherReleaseData } from '../../utils/rideRelease';
import {
  getVehicleTypeLabel,
  getVehicleTypeSeats,
  getCapabilityLabel,
  migrateSpecialVehicleType,
} from '../../config/vehicleTypes';

// Konstanten außerhalb der Komponente für stabile Referenzen
const mapContainerStyle = {
  width: '100%',
  height: '85vh',
  margin: 0,
  padding: 0
};

const mapContainerStyleFullscreen = {
  width: '100%',
  height: '100vh',
  margin: 0,
  padding: 0
};

const defaultCenter = {
  lat: 50.1109,
  lng: 8.6821
};

const defaultOptions = {
  fullscreenControl: false,
  streetViewControl: true,
  mapTypeControl: false,
  mapTypeId: 'roadmap',
  zoomControl: true,
  minZoom: 3,
  maxZoom: 18,
  streetViewControlOptions: {
    position: 3
  },
  mapId: '8bb9c199f544a76'
};

// Padding für fitBounds: links abhängig vom Overlay-Zustand
// (Fahrerliste + Griff + optional aufgeklappte Fahrtenliste).
const BASE_FIT_PADDING = { top: 40, right: 40, bottom: 20, left: 418 };
const RIDES_PANEL_OPEN_LEFT_PADDING = 844;
const AUTO_FIT_PAUSE_MS = 10000;


const STATUS_LABELS = {
  'available':     'Verfügbar',
  'en_route':      'Auf dem Weg',
  'waiting':       'Wartet',
  'with_customer': 'Mit Kunde',
  'paused':        'Pause',
  'offline':       'Offline',
};

const RIDE_STATUS_LABELS = {
  open: 'Angefragt',
  accepted: 'Akzeptiert',
  arrived: 'Angekommen',
  pre_accepted: 'Vom Fahrer angenommen',
  scheduled: 'Geplant',
  in_progress: 'Zielfahrt',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
  cancelled_by_driver: 'Vom Fahrer storniert',
  dispatcher_pending: 'Fahrer angefragt',
  dispatch_exhausted: 'Nicht vermittelbar',
  payment_pending: 'Zahlung ausstehend',
  payment_abandoned: 'Zahlung abgebrochen',
};

const FINAL_RIDE_STATUSES = ['completed', 'cancelled', 'cancelled_by_driver'];
const DUPLICATABLE_RIDE_STATUSES = [
  ...FINAL_RIDE_STATUSES,
  'open',
  'dispatcher_pending',
  'accepted',
  'pre_accepted',
  'arrived',
  'in_progress',
  'scheduled'
];
const ASSIGNABLE_RIDE_STATUSES = ['scheduled', 'open', 'dispatcher_pending', 'pre_accepted', 'accepted'];
const CANCELLABLE_RIDE_STATUSES = ['open', 'scheduled', 'dispatcher_pending', 'accepted', 'arrived', 'pre_accepted', 'in_progress', 'paused'];

const RIDE_DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const PERMISSION_CHECK_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

// "Wartet" – kräftiges Gelb. MUI hat keine Yellow-Palette, deshalb Hex-Wert.
const WAITING_COLOR = '#fbc02d';
const VEHICLE_NUMBER_COLLATOR = new Intl.Collator('de-DE', { numeric: true, sensitivity: 'base' });
const LOCATION_AGE_FRESH_MS = 3 * 60 * 1000;
const LOCATION_AGE_WARNING_MS = 16 * 60 * 1000;

const normalizeMapPoint = (point) => {
  if (!point) return null;
  const lat = Number(point.lat ?? point.latitude);
  const lng = Number(point.lng ?? point.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const getSectorColor = (sector, fallback = '#1976d2') => {
  if (typeof sector?.color === 'string' && sector.color.trim()) {
    return sector.color.trim();
  }
  return fallback;
};

// Status aus isOnline/isAvailable + aktiver Fahrt ableiten
// activeRideStatus: der Status der aktiven Fahrt des Fahrers (oder null)
const deriveDriverStatus = (driver, activeRideStatus) => {
  if (!driver.isOnline) return 'offline';
  if (driver.isAvailable) return 'available';
  // Online aber nicht verfügbar → aus Fahrt-Status ableiten
  if (activeRideStatus === 'in_progress') return 'with_customer';
  if (activeRideStatus === 'arrived') return 'waiting';
  if (activeRideStatus === 'accepted') return 'en_route';
  // Online, nicht verfügbar, keine aktive Fahrt → Pause
  return 'paused';
};

const getStatusBackground = (theme, status) => {
  switch (status) {
    case 'available':     return theme.palette.success.main;
    case 'en_route':      return theme.palette.warning.main;
    case 'waiting':       return WAITING_COLOR;
    case 'with_customer': return theme.palette.error.main;
    case 'paused':        return theme.palette.info.main;
    case 'offline':       return theme.palette.grey[500];
    default:              return undefined;
  }
};

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ theme, status, variant }) => {
  const bg = getStatusBackground(theme, status);
  // "waiting" Gelb braucht dunklen Text für Lesbarkeit.
  const filledTextColor = status === 'waiting' ? 'rgba(0,0,0,0.87)' : (bg ? 'white' : 'inherit');
  const isOutlined = variant === 'outlined';
  return {
    color: isOutlined ? (bg || 'inherit') : filledTextColor,
    backgroundColor: isOutlined ? 'transparent' : bg,
    border: isOutlined && bg ? `1px solid ${bg}` : undefined,
    fontWeight: isOutlined ? 500 : 700,
    '&:hover': {
      backgroundColor: isOutlined && bg ? `${bg}1F` : bg,
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
});

// Roter "!"-Badge für umgewandelte Vorbestellungen
const ConvertedPreorderBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    height: 14,
    minWidth: 14,
    fontSize: '0.6rem',
    padding: '0 4px',
  },
}));

// Kurzes Label für den Fahrzeugtyp inkl. Sitzplatzunterscheidung bei Kleinbussen
const getVehicleTypeChipLabel = (vehicleType) => {
  const typeNum = Number(vehicleType);
  const label = getVehicleTypeLabel(typeNum);
  if (typeNum === 3 || typeNum === 4) {
    return `${label} (${getVehicleTypeSeats(typeNum)})`;
  }
  return label;
};

// Status-Dot für die Fahrerliste
const StatusDot = styled(Box)(({ theme, status }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: getStatusBackground(theme, status) || theme.palette.grey[500],
  flexShrink: 0,
}));

const VehicleNumberBadge = ({ value, compact = false }) => (
  <Typography
    variant="caption"
    component="span"
    sx={{
      minWidth: compact ? 24 : 32,
      px: compact ? 0.4 : 0.5,
      py: compact ? 0 : 0.1,
      borderRadius: 0.75,
      bgcolor: value ? 'primary.main' : 'grey.300',
      color: value ? 'primary.contrastText' : 'text.secondary',
      fontWeight: 700,
      textAlign: 'center',
      lineHeight: compact ? 1.35 : 1.45,
      fontSize: compact ? '0.65rem' : undefined,
      flexShrink: 0,
    }}
  >
    {value || '-'}
  </Typography>
);

const calculateOnlineTime = (driver) => {
  if (!driver?.isOnline) return 'Offline';
  if (!driver.lastLogin) return 'Online seit unbekannt';

  try {
    let lastLoginDate;
    if (driver.lastLogin.toDate) {
      lastLoginDate = driver.lastLogin.toDate();
    } else if (driver.lastLogin.seconds) {
      lastLoginDate = new Date(driver.lastLogin.seconds * 1000);
    } else {
      lastLoginDate = new Date(driver.lastLogin);
    }

    const diffMs = new Date() - lastLoginDate;
    if (diffMs < 0) return 'Gerade eingeloggt';

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `Online seit ${days} Tag(en)`;
    }
    if (hours > 0) return `Online seit ${hours} Std. ${minutes} Min.`;
    if (minutes > 0) return `Online seit ${minutes} Min.`;
    return 'Gerade eingeloggt';
  } catch (error) {
    console.error('Fehler bei Online-Zeit-Berechnung:', error, driver);
    return 'Online seit unbekannt';
  }
};

const getTimestampMs = (timestamp) => {
  if (!timestamp) return null;
  try {
    if (timestamp.toDate) {
      const ms = timestamp.toDate().getTime();
      return Number.isFinite(ms) ? ms : null;
    }

    if (timestamp.seconds != null) {
      const ms = timestamp.seconds * 1000;
      return Number.isFinite(ms) ? ms : null;
    }

    const ms = new Date(timestamp).getTime();
    return Number.isFinite(ms) ? ms : null;
  } catch (error) {
    return null;
  }
};

const getLocationAgeColor = (timestamp, nowMs = Date.now()) => {
  const timestampMs = getTimestampMs(timestamp);
  if (timestampMs == null) return 'error.main';

  const ageMs = nowMs - timestampMs;
  if (ageMs < LOCATION_AGE_FRESH_MS) return 'info.main';
  if (ageMs < LOCATION_AGE_WARNING_MS) return WAITING_COLOR;
  return 'error.main';
};

const formatLocationAge = (timestamp) => {
  const timestampMs = getTimestampMs(timestamp);
  if (timestampMs == null) return 'Letztes Update: unbekannt';
  try {
    const diffMs = Date.now() - timestampMs;
    if (!Number.isFinite(diffMs)) return 'Letztes Update: unbekannt';
    if (diffMs < 0) return 'Letztes Update: gerade eben';
    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 5) return 'Letztes Update: gerade eben';
    if (seconds < 60) return `Letztes Update: vor ${seconds} Sek.`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `Letztes Update: vor ${minutes} Min.`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Letztes Update: vor ${hours} Std. ${minutes % 60} Min.`;
    const days = Math.floor(hours / 24);
    return `Letztes Update: vor ${days} Tag(en)`;
  } catch (error) {
    return 'Letztes Update: unbekannt';
  }
};

const LOCATION_PERMISSION_LABELS = {
  always: 'Immer zulassen',
  whileInUse: 'Beim Verwenden',
  denied: 'Verweigert',
  deniedForever: 'Dauerhaft verweigert',
  unableToDetermine: 'Nicht bestimmbar',
  unknown: 'Unbekannt',
};

const LOCATION_PERMISSION_SOURCE_LABELS = {
  vehicle_selection_permission_check: 'Fahrzeugauswahl',
  home_driver_get_current_location: 'HomeDriver Standortabruf',
  home_driver_background_location_request: 'Background-Anfrage',
  home_driver_login_location_write: 'Login-Standortupdate',
  home_driver_position_stream: 'Live-Standortstream',
  home_driver_followup_location_write: 'Anschlusstour-Update',
  home_driver_order_location_write: 'Fahrt-Standortupdate',
  workmanager_periodic: 'WorkManager periodisch',
  workmanager_wakeup: 'WorkManager Wake-up',
};

const BATTERY_OPTIMIZATION_SOURCE_LABELS = {
  battery_optimization_dialog_check: 'Akku-Dialog Prüfung',
  battery_optimization_dialog_request: 'Akku-Dialog Anfrage',
  battery_optimization_dialog_later: 'Akku-Dialog später',
  notification_readiness_check: 'Readiness-Prüfung',
  notification_readiness_resolve: 'Readiness-Aktion',
};

const formatPermissionStatus = (status) => (
  LOCATION_PERMISSION_LABELS[status] || status || 'Unbekannt'
);

const formatYesNoUnknown = (value) => {
  if (value === true) return 'Ja';
  if (value === false) return 'Nein';
  return 'Unbekannt';
};

const formatPermissionCheckedAt = (timestamp) => {
  const timestampMs = getTimestampMs(timestamp);
  if (timestampMs == null) return 'unbekannt';
  return PERMISSION_CHECK_FORMATTER.format(new Date(timestampMs));
};

// Relative Dauer seit einem Zeitpunkt, z. B. "25 Min." — kompakt für den
// App-Status im Live-Tooltip.
const formatDurationSince = (timestampMs) => {
  const diffMs = Date.now() - timestampMs;
  if (!Number.isFinite(diffMs) || diffMs < 0) return null;
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return `${seconds} Sek.`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} Std. ${minutes % 60} Min.`;
  return `${Math.floor(hours / 24)} Tag(en)`;
};

// App-Status (Vorder-/Hintergrund) inkl. "seit wann". foregroundSince gibt es
// erst ab App-Version 07/2026; bei älteren Apps fehlt dann die Zeitangabe.
const formatAppForegroundState = (inForeground, foregroundSince, backgroundSince) => {
  if (inForeground !== true && inForeground !== false) return 'Unbekannt';
  const label = inForeground ? 'Vordergrund' : 'Hintergrund';
  const sinceMs = getTimestampMs(inForeground ? foregroundSince : backgroundSince);
  if (sinceMs == null) return label;
  const duration = formatDurationSince(sinceMs);
  const absolute = PERMISSION_CHECK_FORMATTER.format(new Date(sinceMs));
  return duration != null
    ? `${label} (seit ${absolute}, vor ${duration})`
    : `${label} (seit ${absolute})`;
};

const formatPermissionSource = (source) => (
  LOCATION_PERMISSION_SOURCE_LABELS[source] || source || 'unbekannt'
);

const formatBatteryOptimizationStatus = (ignored, status) => {
  if (ignored === true) return 'Ausgenommen';
  if (ignored === false) return `Aktiv (${status || 'unbekannt'})`;
  return status || 'Unbekannt';
};

const formatBatteryOptimizationSource = (source) => (
  BATTERY_OPTIMIZATION_SOURCE_LABELS[source] || source || 'unbekannt'
);

const isAutoAcceptedOpenPreOrder = (ride) =>
  ride.orderType === 1 &&
  ride.autoAccepted === true &&
  ride.status === 'scheduled' &&
  !ride.driver &&
  !ride.driverId &&
  !ride.isPhoneCustomer;

const getRideDisplayStatus = (ride) => {
  if (ride?.status === 'open' && ride?.dispatchState === 'exhausted') {
    return 'dispatch_exhausted';
  }
  return ride?.status;
};

const getRideChipColor = (status) => {
  switch (status) {
    case 'open':
    case 'dispatcher_pending':
      return 'warning';
    case 'accepted':
      return 'info';
    case 'arrived':
    case 'in_progress':
      return 'primary';
    case 'scheduled':
    case 'pre_accepted':
      return 'secondary';
    case 'dispatch_exhausted':
    case 'cancelled':
    case 'cancelled_by_driver':
      return 'error';
    case 'completed':
      return 'success';
    default:
      return 'default';
  }
};

const formatRideDateTime = (dateInput) => {
  const ms = toMs(dateInput);
  if (ms === null) return 'N/A';
  return RIDE_DATE_FORMATTER.format(new Date(ms));
};

const getRidePrimaryTimeLabel = (ride) => {
  if (ride.orderType === 1 || ride.scheduledTime || ride.scheduled_time) {
    return `Geplant: ${formatRideDateTime(ride.scheduledTime || ride.scheduled_time)}`;
  }
  return `Eingang: ${formatRideDateTime(ride.timestamp || ride.request_time)}`;
};

const getDriverDisplayName = (driver) =>
  driver?.displayName || driver?.name || driver?.user?.name || driver?.email || null;

const shortId = (value) => {
  if (!value || typeof value !== 'string') return null;
  return value.length > 10 ? `${value.substring(0, 10)}...` : value;
};

const getRideDriverName = (ride, driverById) => {
  if (ride.driverName) return ride.driverName;
  if (ride.driver?.user?.name) return ride.driver.user.name;
  if (ride.driverId && driverById[ride.driverId]) return getDriverDisplayName(driverById[ride.driverId]);
  if (ride.currentOfferDriverId) {
    const offeredDriver = driverById[ride.currentOfferDriverId];
    const offeredDriverName =
      getDriverDisplayName(offeredDriver) ||
      ride.currentOfferDriverName ||
      shortId(ride.currentOfferDriverId);
    return offeredDriverName || null;
  }
  return null;
};

const getRideVehicleNumber = (ride, driverById, vehicleByDriverId) => {
  const displayDriverId = ride.driverId || ride.currentOfferDriverId || null;
  const driverVehicle = displayDriverId ? vehicleByDriverId[displayDriverId] : null;
  const driver = displayDriverId ? driverById[displayDriverId] : null;
  const car = driver?.car || null;
  return ride.vehicle?.vehicleNumber || driverVehicle?.vehicleNumber || car?.vehicleNumber || null;
};

const getRideVehiclePlate = (ride, driverById, vehicleByDriverId) => {
  const displayDriverId = ride.driverId || ride.currentOfferDriverId || null;
  const driverVehicle = displayDriverId ? vehicleByDriverId[displayDriverId] : null;
  const driver = displayDriverId ? driverById[displayDriverId] : null;
  const car = driver?.car || null;
  return ride.driverLicensePlate || ride.vehicle?.licensePlate || driverVehicle?.licensePlate || car?.licensePlate || null;
};

const getShortAddress = (value) => value || 'Keine Adresse';

// Aktive Fahrtstati, für die wir die Strecke einblenden.
const ACTIVE_RIDE_STATUSES_FOR_ROUTE = ['accepted', 'arrived', 'in_progress'];

// Fahrten ohne zugewiesenen Fahrer, für die wir trotzdem die Strecke
// (Abholort → Ziel) anzeigen können — Sofortbestellungen ("open") und
// Vorbestellungen ("scheduled").
const UNASSIGNED_RIDE_STATUSES_FOR_ROUTE = ['open', 'scheduled'];

// Farben für die Route: vor Abholung folgt die Strecke der "en_route"-Farbe
// des Fahrer-Markers; ab Abholort wird die Zielfahrt blau dargestellt.
const ROUTE_LEG_COLORS = {
  to_pickup: '#F59E0B',
  to_destination: '#1D4ED8',
};
const SELECTED_RIDE_BACKGROUND = 'rgba(29, 78, 216, 0.14)';
const SELECTED_RIDE_HOVER_BACKGROUND = 'rgba(29, 78, 216, 0.2)';

const isValidRideCoordinate = (lat, lng) =>
  Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);

const getRidePickupPosition = (ride) => {
  if (!ride) return null;
  const lat = Number(ride.startLatitude ?? ride.pickup_latitude ?? ride.pickupLatitude);
  const lng = Number(ride.startLongitude ?? ride.pickup_longitude ?? ride.pickupLongitude);
  if (!isValidRideCoordinate(lat, lng)) return null;
  return { lat, lng };
};

const getRideDestinationPosition = (ride) => {
  if (!ride) return null;
  const lat = Number(ride.destinationLatitude ?? ride.destination_latitude);
  const lng = Number(ride.destinationLongitude ?? ride.destination_longitude);
  if (!isValidRideCoordinate(lat, lng)) return null;
  return { lat, lng };
};

const LiveMap = React.memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Daten aus AppDataManager (bereits Echtzeit via onSnapshot)
  const drivers = useSelector(state => state.appData.drivers);
  const vehicles = useSelector(state => state.appData.vehicles);
  const rides = useSelector(state => state.appData.rides);
  const driversLoading = useSelector(state => state.appData.isLoading.drivers);
  const driversError = useSelector(state => state.appData.error.drivers);
  const settings = useSelector(state => state.settings.global);
  const settingsInitialized = useSelector(state => state.settings.isInitialized);
  const sectors = useSelector(state => state.sectors.sectors);
  const sectorsLoading = useSelector(state => state.sectors.isLoading);
  const currentUser = useSelector(state => state.auth.user);
  // Sub-Unternehmen dürfen per Rules (R2) nur die eigenen Fahrer-Standorte lesen.
  const driverLocationOperatorScope = isOperatorRole(currentUser) ? (currentUser?.operatorId || null) : null;

  const [map, setMap] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [selectedRideId, setSelectedRideId] = useState(null);
  const [routeLegs, setRouteLegs] = useState([]);
  const [selectedSectorId, setSelectedSectorId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [driverLocations, setDriverLocations] = useState({});
  const [locationAgeNowMs, setLocationAgeNowMs] = useState(() => Date.now());
  const [searchText, setSearchText] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  // Welcher Abschnitt im Listen-Panel ist aufgeklappt? Exklusiv: das Öffnen
  // einer Ansicht klappt die andere zu, damit das Overlay kompakt bleibt.
  // 'drivers' = Liste nach Fahrer, 'sectors' = Liste nach Sektoren, null = beide zu.
  const [expandedSection, setExpandedSection] = useState('drivers');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [ridesPanelOpen, setRidesPanelOpen] = useState(true);
  const [rideMenuAnchor, setRideMenuAnchor] = useState(null);
  const [rideMenuRide, setRideMenuRide] = useState(null);

  const unsubscribeRef = useRef(null);
  const mapContainerRef = useRef(null);
  const autoFitPauseUntilRef = useRef(0);
  // Hält die aktuell auf der Karte gezeichneten Routen-Polylines (raw
  // google.maps.Polyline-Instanzen). Wird beim Status-/Auswahl-Wechsel
  // synchron mit setMap(null) abgeräumt, damit nie veraltete Linien stehen
  // bleiben (der React-Wrapper hatte hier Reconciliations-Probleme).
  const routePolylinesRef = useRef([]);
  // Phasenschlüssel der letzten erfolgreich gestarteten Directions-Anfrage.
  // Analog zur Mobile-App: pro (Fahrt, Status) genau ein API-Call. Der
  // Fahrer-Marker bewegt sich lokal auf der bereits geholten Polyline.
  const lastFetchedPhaseKeyRef = useRef(null);
  // Welcher Modus liegt der aktuellen Fahrtauswahl zugrunde?
  //   'driver'     → der Fahrer ist parallel fokussiert; verliert er den
  //                  Fokus, wird auch die Fahrtauswahl beendet.
  //   'unassigned' → reine Fahrt-Ansicht ohne Fahrerfokus (Sofort/Vor-
  //                  bestellung oder dispatcher_pending ohne Position).
  //   null         → keine Fahrtauswahl.
  const rideFocusModeRef = useRef(null);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setLocationAgeNowMs(Date.now());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!mapContainerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      mapContainerRef.current.requestFullscreen();
    }
  }, []);

  const isSectorDispatchActive = settings?.sofortbestellungAnzeigeMethode === 'sectors';

  useEffect(() => {
    if (!settingsInitialized) {
      dispatch(fetchSettings());
    }
  }, [dispatch, settingsInitialized]);

  useEffect(() => {
    if (isSectorDispatchActive && sectors.length === 0 && !sectorsLoading) {
      dispatch(fetchSectors());
    }
  }, [dispatch, isSectorDispatchActive, sectors.length, sectorsLoading]);

  // Google Maps API laden
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: window.GOOGLE_MAPS_LIBRARIES,
    version: 'weekly'
  });

  // Echtzeit-Standort-Listener. Zentrale Rollen: alle Fahrer. Sub-Unternehmen:
  // nur die eigenen (operatorId) — sonst bräche die Query an den Rules (R2).
  useEffect(() => {
    const unsubscribe = driverLocationOperatorScope
      ? LocationService.subscribeToOperatorDriverLocations(
          driverLocationOperatorScope,
          (locations) => setDriverLocations(locations)
        )
      : LocationService.subscribeToAllDriverLocations(
          (locations) => setDriverLocations(locations)
        );
    unsubscribeRef.current = unsubscribe;

    return () => {
      if (typeof unsubscribeRef.current === 'function') {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [driverLocationOperatorScope]);

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  // Map: driverId → aktiver Fahrtstatus (accepted, arrived, in_progress)
  const activeRideByDriver = useMemo(() => {
    const map = {};
    const activeStatuses = ['accepted', 'arrived', 'in_progress'];
    rides.forEach(ride => {
      if (ride.driverId && activeStatuses.includes(ride.status)) {
        // Bei mehreren aktiven Fahrten gewinnt die "höchste" (in_progress > arrived > accepted)
        const priority = { in_progress: 3, arrived: 2, accepted: 1 };
        if (!map[ride.driverId] || priority[ride.status] > (priority[map[ride.driverId]] || 0)) {
          map[ride.driverId] = ride.status;
        }
      }
    });
    return map;
  }, [rides]);

  const vehicleByDriverId = useMemo(() => {
    return vehicles.reduce((map, vehicle) => {
      const driverId = vehicle.driverId || vehicle.current_driver?.id || vehicle.current_driver_id || null;
      if (driverId) {
        map[driverId] = vehicle;
      }
      return map;
    }, {});
  }, [vehicles]);

  const activeSectors = useMemo(() => {
    if (!isSectorDispatchActive) return [];
    return sectors
      .filter(sector => sector.active !== false)
      .map(sector => ({
        ...sector,
        polygonPath: Array.isArray(sector.polygon)
          ? sector.polygon.map(normalizeMapPoint).filter(Boolean)
          : [],
        centerPoint: normalizeMapPoint(sector.centroid) || (
          Array.isArray(sector.polygon)
            ? sector.polygon.map(normalizeMapPoint).filter(Boolean)[0]
            : null
        )
      }))
      .filter(sector => sector.polygonPath.length >= 3);
  }, [isSectorDispatchActive, sectors]);

  const sectorById = useMemo(() => {
    return activeSectors.reduce((map, sector) => {
      map[sector.id] = sector;
      return map;
    }, {});
  }, [activeSectors]);

  // Fahrer mit abgeleitetem Status und Position anreichern
  const enrichedDrivers = useMemo(() => {
    return drivers.map(driver => {
      const activeRideStatus = activeRideByDriver[driver.id] || null;
      const status = deriveDriverStatus(driver, activeRideStatus);
      const realtimeLocation = driverLocations[driver.id];
      const isValidCoord = (lat, lng) => lat != null && lng != null && !(lat === 0 && lng === 0);

      const hasLocation = (realtimeLocation && isValidCoord(realtimeLocation.latitude, realtimeLocation.longitude)) ||
                         (driver.location && isValidCoord(driver.location.latitude, driver.location.longitude));

      const position = (realtimeLocation && isValidCoord(realtimeLocation.latitude, realtimeLocation.longitude))
        ? { lat: realtimeLocation.latitude, lng: realtimeLocation.longitude }
        : (driver.location && isValidCoord(driver.location.latitude, driver.location.longitude))
          ? { lat: driver.location.latitude, lng: driver.location.longitude }
          : null;

      const displayName = driver.name || driver.user?.name || 'Unbekannter Fahrer';
      const assignedVehicle = vehicleByDriverId[driver.id] || driver.vehicle || null;
      const carData = driver.car || null;
      const vehicleNumber = (
        assignedVehicle?.vehicleNumber ||
        carData?.vehicleNumber ||
        driver.vehicleNumber ||
        // Letzter Fallback: die von der Fahrer-App selbst gepflegte Nummer
        // aus driverLocations — zeigt auch dann den Chip, wenn die
        // Vehicles-Zuweisung inkonsistent ist (z. B. nach Duplikat-
        // Kennzeichen-Übernahme oder Fremd-Freigabe).
        realtimeLocation?.vehicleNumber ||
        ''
      ).toString().trim();
      const vehicleModel = assignedVehicle?.model || carData?.model || '';
      const vehicleLicensePlate = assignedVehicle?.licensePlate || carData?.licensePlate || '';
      // Initialen für den Marker (z.B. "Max Mustermann" → "MM")
      const nameParts = displayName.split(' ').filter(Boolean);
      const initials = nameParts.length >= 2
        ? (nameParts[0][0] + nameParts[nameParts.length - 1][0])
        : displayName.substring(0, 2);

      return {
        ...driver,
        derivedStatus: status,
        hasLocation,
        position,
        isRealtime: !!realtimeLocation,
        displayName,
        initials: initials.toUpperCase(),
        vehicleNumber,
        markerLabel: vehicleNumber || initials.toUpperCase(),
        vehicleInfo: assignedVehicle || carData
          ? `${vehicleNumber ? `${vehicleNumber} · ` : ''}${vehicleModel} ${vehicleLicensePlate ? `(${vehicleLicensePlate})` : ''}`.trim()
          : null,
        vehicleDescription: assignedVehicle || carData
          ? `${vehicleModel} ${vehicleLicensePlate ? `(${vehicleLicensePlate})` : ''}`.trim()
          : null,
        streetName: driverLocations[driver.id]?.streetName || null,
        locationAddress: (
          driverLocations[driver.id]?.formattedAddress ||
          [
            driverLocations[driver.id]?.streetName,
            [driverLocations[driver.id]?.postalCode, driverLocations[driver.id]?.locality].filter(Boolean).join(' ')
          ].filter(Boolean).join(', ') ||
          null
        ),
        // Frische bevorzugt aus der Gerätezeit des Schreibzeitpunkts
        // (deviceTimestampMs, App ≥ 07/2026): Firestore-Offline-Writes werden
        // beim Netz-Comeback mit FRISCHEM serverTimestamp eingespielt — nur
        // die Gerätezeit verrät, dass der Punkt alt ist. Fallback auf den
        // serverTimestamp für ältere App-Versionen.
        lastLocationTimestamp: realtimeLocation?.deviceTimestampMs ?? realtimeLocation?.timestamp ?? null,
        appVersion: realtimeLocation?.appVersion || driver.appVersion || null,
        appPlatform: realtimeLocation?.appPlatform || driver.appPlatform || null,
        appInForeground: realtimeLocation?.appInForeground,
        appForegroundSince: realtimeLocation?.foregroundSince ?? null,
        appBackgroundSince: realtimeLocation?.backgroundSince ?? null,
        locationPermissionStatus: realtimeLocation?.locationPermissionStatus || null,
        locationPermissionAllowsForeground: realtimeLocation?.locationPermissionAllowsForeground,
        locationPermissionAllowsBackground: realtimeLocation?.locationPermissionAllowsBackground,
        locationServiceEnabled: realtimeLocation?.locationServiceEnabled,
        locationPermissionPlatform: realtimeLocation?.locationPermissionPlatform || realtimeLocation?.appPlatform || driver.appPlatform || null,
        locationPermissionSource: realtimeLocation?.locationPermissionSource || null,
        locationPermissionCheckedAt: realtimeLocation?.locationPermissionCheckedAt || null,
        locationPermissionCheckError: realtimeLocation?.locationPermissionCheckError || null,
        batteryOptimizationIgnored: realtimeLocation?.batteryOptimizationIgnored,
        batteryOptimizationStatus: realtimeLocation?.batteryOptimizationStatus || null,
        batteryOptimizationSource: realtimeLocation?.batteryOptimizationSource || null,
        batteryOptimizationCheckedAt: realtimeLocation?.batteryOptimizationCheckedAt || null,
        batteryOptimizationRequestResult: realtimeLocation?.batteryOptimizationRequestResult || null,
        batteryOptimizationCheckError: realtimeLocation?.batteryOptimizationCheckError || null,
        currentSectorId: realtimeLocation?.currentSectorId || driver.currentSectorId || null,
        currentSectorCode: realtimeLocation?.currentSectorCode || driver.currentSectorCode || null,
        currentSectorName: realtimeLocation?.currentSectorName || driver.currentSectorName || null,
        sectorQueuePosition: realtimeLocation?.sectorQueuePosition || driver.sectorQueuePosition || null,
        sectorQueueTotal: realtimeLocation?.sectorQueueTotal || driver.sectorQueueTotal || null,
        targetSectorId: realtimeLocation?.targetSectorId || null,
        targetSectorCode: realtimeLocation?.targetSectorCode || null,
        targetSectorName: realtimeLocation?.targetSectorName || null,
      };
    });
  }, [drivers, driverLocations, activeRideByDriver, vehicleByDriverId]);

  const enrichedDriverById = useMemo(() => {
    return enrichedDrivers.reduce((map, driver) => {
      if (driver.id) map[driver.id] = driver;
      return map;
    }, {});
  }, [enrichedDrivers]);

  const liveMapRides = useMemo(() => getLiveMapRelevantRides(rides), [rides]);

  // Sektor-Gruppierung für die Sektor-Ansicht. Wird unten anhand der bereits
  // gefilterten Fahrerliste neu berechnet, damit die Filter-Chips/Suche auch
  // hier wirken. Diese Variante (auf Basis aller Fahrer) wird nur noch für
  // den Selected-Sector-Auto-Focus genutzt.
  const sectorDriverGroupsAll = useMemo(() => {
    if (!isSectorDispatchActive) return [];
    return activeSectors.map(sector => {
      const sectorDrivers = enrichedDrivers
        .filter(driver => driver.currentSectorId === sector.id && driver.derivedStatus !== 'offline')
        .sort((a, b) => {
          const posA = Number(a.sectorQueuePosition) || Number.MAX_SAFE_INTEGER;
          const posB = Number(b.sectorQueuePosition) || Number.MAX_SAFE_INTEGER;
          if (posA !== posB) return posA - posB;
          return (a.displayName || '').localeCompare(b.displayName || '');
        });
      return {
        sector,
        drivers: sectorDrivers,
        availableCount: sectorDrivers.filter(driver => driver.derivedStatus === 'available').length
      };
    });
  }, [isSectorDispatchActive, activeSectors, enrichedDrivers]);

  // Gefilterte und sortierte Fahrer
  const filteredDrivers = useMemo(() => {
    return enrichedDrivers.filter(driver => {
      // Statusfilter
      if (filterStatus === 'offline') {
        if (driver.derivedStatus !== 'offline') return false;
      } else {
        if (driver.derivedStatus === 'offline') return false;
        if (filterStatus !== 'all' && driver.derivedStatus !== filterStatus) return false;
      }
      // Suchfilter
      if (searchText) {
        const search = searchText.toLowerCase();
        const nameMatch = driver.displayName.toLowerCase().includes(search);
        const vehicleNumberMatch = driver.vehicleNumber?.toLowerCase().includes(search);
        const vehicleMatch = driver.vehicleInfo?.toLowerCase().includes(search);
        const locationMatch = driver.locationAddress?.toLowerCase().includes(search);
        const phoneMatch = driver.phone?.toLowerCase().includes(search);
        if (!nameMatch && !vehicleNumberMatch && !vehicleMatch && !locationMatch && !phoneMatch) return false;
      }
      return true;
    }).sort((a, b) => {
      if (a.vehicleNumber && b.vehicleNumber) {
        const vehicleNumberDiff = VEHICLE_NUMBER_COLLATOR.compare(a.vehicleNumber, b.vehicleNumber);
        if (vehicleNumberDiff !== 0) return vehicleNumberDiff;
      } else if (a.vehicleNumber || b.vehicleNumber) {
        return a.vehicleNumber ? -1 : 1;
      }
      return (a.displayName || '').localeCompare(b.displayName || '');
    });
  }, [enrichedDrivers, filterStatus, searchText]);

  // Fahrer mit Standort für die Karte (Offline-Fahrer nur bei expliziter Filterauswahl)
  const driversWithLocation = useMemo(() => {
    return filteredDrivers.filter(d => d.hasLocation && (filterStatus === 'offline' || d.derivedStatus !== 'offline'));
  }, [filteredDrivers, filterStatus]);

  // Sektor-Gruppierung für die linke "Nach Sektoren"-Liste – nutzt
  // filteredDrivers, damit Filter-Chips und Suche auch hier wirken.
  // Offline-Fahrer werden in der Sektor-Ansicht grundsätzlich ausgeblendet,
  // auch wenn der Status-Filter "Offline" gewählt ist – sie haben keine
  // aktive Sektor-Anmeldung, die hier sinnvoll darstellbar wäre.
  const sectorDriverGroups = useMemo(() => {
    if (!isSectorDispatchActive) return [];
    return activeSectors.map(sector => {
      const sectorDrivers = filteredDrivers
        .filter(driver => driver.derivedStatus !== 'offline' && driver.currentSectorId === sector.id)
        .sort((a, b) => {
          const posA = Number(a.sectorQueuePosition) || Number.MAX_SAFE_INTEGER;
          const posB = Number(b.sectorQueuePosition) || Number.MAX_SAFE_INTEGER;
          if (posA !== posB) return posA - posB;
          return (a.displayName || '').localeCompare(b.displayName || '');
        });
      return {
        sector,
        drivers: sectorDrivers,
        availableCount: sectorDrivers.filter(driver => driver.derivedStatus === 'available').length
      };
    }).sort((a, b) => {
      const driverCountDiff = b.drivers.length - a.drivers.length;
      if (driverCountDiff !== 0) return driverCountDiff;

      const sectorA = (a.sector.code || a.sector.name || '').toString();
      const sectorB = (b.sector.code || b.sector.name || '').toString();
      return VEHICLE_NUMBER_COLLATOR.compare(sectorA, sectorB);
    });
  }, [isSectorDispatchActive, activeSectors, filteredDrivers]);

  // Fahrer ohne Sektor-Anmeldung – eigener Abschnitt in der Sektor-Ansicht.
  // Auch hier nur Online-Fahrer, da Offline-Fahrer per Definition keinen Sektor haben.
  const driversWithoutSector = useMemo(() => {
    if (!isSectorDispatchActive) return [];
    return filteredDrivers.filter(driver => driver.derivedStatus !== 'offline' && !driver.currentSectorId);
  }, [isSectorDispatchActive, filteredDrivers]);

  // Status-Zusammenfassung für Chips
  const statusCounts = useMemo(() => {
    const counts = { all: 0, available: 0, en_route: 0, waiting: 0, with_customer: 0, paused: 0, offline: 0 };
    enrichedDrivers.forEach(d => {
      if (d.derivedStatus === 'offline') {
        counts.offline++;
        return;
      }

      counts.all++;
      if (counts[d.derivedStatus] !== undefined) {
        counts[d.derivedStatus]++;
      }
    });
    return counts;
  }, [enrichedDrivers]);

  // Ref hält immer die aktuellste Fahrerliste mit Standort, damit fitBounds
  // nicht bei jedem Standort-Update ausgelöst wird.
  const driversWithLocationRef = useRef(driversWithLocation);
  driversWithLocationRef.current = driversWithLocation;

  const hasInitialFitRef = useRef(false);
  const selectedDriverIdRef = useRef(selectedDriverId);
  selectedDriverIdRef.current = selectedDriverId;
  const selectedRideIdRef = useRef(selectedRideId);
  selectedRideIdRef.current = selectedRideId;

  const getCurrentFitPadding = useCallback(() => {
    const mapWidth = map?.getDiv()?.offsetWidth || 0;
    const desiredLeft = ridesPanelOpen ? RIDES_PANEL_OPEN_LEFT_PADDING : BASE_FIT_PADDING.left;
    const maxLeft = mapWidth > 0 ? Math.max(BASE_FIT_PADDING.left, Math.floor(mapWidth * 0.68)) : desiredLeft;

    return {
      ...BASE_FIT_PADDING,
      left: Math.min(desiredLeft, maxLeft),
    };
  }, [map, ridesPanelOpen]);

  // Zentriert eine Position in der Nutzfläche (Karte minus dynamischem Padding).
  // `panTo` allein setzt den Punkt in die Mitte der Gesamtkarte — durch das
  // Listen-Overlay links wirkt er dann aber nach oben links verschoben.
  // `panBy` kompensiert den Padding-Versatz (Δlinks/rechts, Δoben/unten)/2.
  const panToCenteredInUsableArea = useCallback((position) => {
    if (!map || !position) return;
    const fitPadding = getCurrentFitPadding();
    map.panTo(position);
    const offsetX = (fitPadding.left - fitPadding.right) / 2;
    const offsetY = (fitPadding.top - fitPadding.bottom) / 2;
    if (offsetX !== 0 || offsetY !== 0) {
      map.panBy(-offsetX, -offsetY);
    }
  }, [map, getCurrentFitPadding]);

  // Ref hält die zusätzlichen Bounds-Punkte (z.B. Abhol-/Zielort einer
  // ausgewählten Fahrt), damit applyFitBounds aktuelle Werte sieht, ohne
  // dass der Callback bei jeder Positions-Änderung neu erzeugt wird.
  const extraBoundsPointsRef = useRef([]);

  const applyFitBounds = useCallback(() => {
    const drivers = driversWithLocationRef.current;
    const extras = extraBoundsPointsRef.current;
    if (!map || (drivers.length === 0 && extras.length === 0)) return;
    const fitPadding = getCurrentFitPadding();

    // Einzelner Fahrer ohne zusätzliche Punkte: kein fitBounds (würde auf
    // einen Punkt maxZoomen). Stattdessen Zoom halten/setzen und den Punkt
    // mittig in der sichtbaren Nutzfläche platzieren.
    if (drivers.length === 1 && extras.length === 0) {
      const currentZoom = map.getZoom();
      if (currentZoom == null || currentZoom < 14 || currentZoom > 16) {
        map.setZoom(15);
      }
      panToCenteredInUsableArea(drivers[0].position);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    drivers.forEach(driver => bounds.extend(driver.position));
    extras.forEach(point => bounds.extend(point));
    map.fitBounds(bounds, fitPadding);
  }, [map, panToCenteredInUsableArea, getCurrentFitPadding]);

  const pauseAutoFit = useCallback(() => {
    autoFitPauseUntilRef.current = Date.now() + AUTO_FIT_PAUSE_MS;
  }, []);

  useEffect(() => {
    if (!map) return undefined;
    const mapDiv = map.getDiv();
    if (!mapDiv) return undefined;

    const pauseOnTouchZoom = (event) => {
      if (event.touches && event.touches.length < 2) return;
      pauseAutoFit();
    };

    mapDiv.addEventListener('wheel', pauseAutoFit, { passive: true });
    mapDiv.addEventListener('click', pauseAutoFit);
    mapDiv.addEventListener('dblclick', pauseAutoFit);
    mapDiv.addEventListener('touchstart', pauseOnTouchZoom, { passive: true });

    return () => {
      mapDiv.removeEventListener('wheel', pauseAutoFit);
      mapDiv.removeEventListener('click', pauseAutoFit);
      mapDiv.removeEventListener('dblclick', pauseAutoFit);
      mapDiv.removeEventListener('touchstart', pauseOnTouchZoom);
    };
  }, [map, pauseAutoFit]);

  // Initiales Einpassen: Genau einmal, sobald Karte und Fahrer verfügbar sind
  useEffect(() => {
    if (!map || hasInitialFitRef.current) return;
    if (driversWithLocation.length === 0) return;
    hasInitialFitRef.current = true;
    applyFitBounds();
  }, [map, driversWithLocation, applyFitBounds]);

  // Bei Filter-Änderung erneut einpassen — aber Follow-Modus respektieren,
  // wenn der ausgewählte Fahrer weiterhin im Filter enthalten ist.
  useEffect(() => {
    if (!map || !hasInitialFitRef.current) return;
    const selectedId = selectedDriverIdRef.current;
    const drivers = driversWithLocationRef.current;
    if (selectedId && drivers.some(d => d.id === selectedId)) return;
    applyFitBounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  // Periodische Prüfung: Passe Karte an, wenn sie zu weit raus- oder reingezoomt
  // ist. Timer-basiert statt reaktiv, damit es auch bei stationären Fahrern greift.
  // Pausiert im Follow-Modus (ausgewählter Fahrer).
  useEffect(() => {
    if (!map) return undefined;

    const checkAndMaybeFit = () => {
      if (!hasInitialFitRef.current) return;
      // Bei aktiver Fahrt-Auswahl darf das Auto-Fit weiterlaufen, damit
      // Fahrer + Abholort + Ziel im Bild bleiben, auch wenn der Fahrer sich
      // bewegt. Ohne Fahrtauswahl pausieren wir bei einem gewählten Fahrer
      // wie zuvor (Follow-Driver-Modus).
      if (selectedDriverIdRef.current && !selectedRideIdRef.current) return;
      if (Date.now() < autoFitPauseUntilRef.current) return;
      const drivers = driversWithLocationRef.current;
      const extras = extraBoundsPointsRef.current;
      if (drivers.length === 0 && extras.length === 0) return;

      const currentBounds = map.getBounds();
      if (!currentBounds) return;

      const anyOutside =
        drivers.some(d => !currentBounds.contains(d.position)) ||
        extras.some(p => !currentBounds.contains(p));

      // Nur ein einzelner Punkt insgesamt: nur refitten, wenn er aus dem
      // sichtbaren Bereich läuft.
      if (drivers.length + extras.length === 1) {
        if (!anyOutside) return;
        applyFitBounds();
        return;
      }

      const neededBounds = new window.google.maps.LatLngBounds();
      drivers.forEach(d => neededBounds.extend(d.position));
      extras.forEach(p => neededBounds.extend(p));
      const currentSpan = currentBounds.toSpan();
      const neededSpan = neededBounds.toSpan();
      const latRatio = neededSpan.lat() / Math.max(currentSpan.lat(), 1e-6);
      const lngRatio = neededSpan.lng() / Math.max(currentSpan.lng(), 1e-6);
      const maxRatio = Math.max(latRatio, lngRatio);

      // Dynamische Schwelle basierend auf Padding: Nach einem Fit entspricht
      // `maxRatio` im Ruhezustand ungefähr dem Verhältnis „Nutzfläche/Gesamtfläche"
      // der Karte (engere Achse). Wir schlagen 15 % Sicherheitsabstand zum
      // Ruhezustand auf — verhindert Oszillation auf schmalen Viewports und
      // bleibt dennoch responsiv, wenn Fahrer konvergieren.
      const mapDiv = map.getDiv();
      const fitPadding = getCurrentFitPadding();
      const totalW = mapDiv.offsetWidth || 1;
      const totalH = mapDiv.offsetHeight || 1;
      const usableRatio = Math.min(
        (totalW - fitPadding.left - fitPadding.right) / totalW,
        (totalH - fitPadding.top - fitPadding.bottom) / totalH
      );
      const threshold = Math.max(usableRatio * 0.85, 0.4);
      const tooZoomedOut = maxRatio < threshold;

      if (!anyOutside && !tooZoomedOut) return;
      applyFitBounds();
    };

    const interval = setInterval(checkAndMaybeFit, 3000);
    return () => clearInterval(interval);
  }, [map, applyFitBounds, getCurrentFitPadding]);

  // Ausgewählter Fahrer aus aktueller Liste ableiten, damit Position/Status
  // stets aktuell sind (folgt der Echtzeit-Aktualisierung).
  const selectedDriver = useMemo(() => {
    if (!selectedDriverId) return null;
    return enrichedDrivers.find(d => d.id === selectedDriverId) || null;
  }, [selectedDriverId, enrichedDrivers]);

  const selectedDriverActiveRide = useMemo(() => {
    if (!selectedDriver) return null;
    const driverRides = rides.filter(r => r.driverId === selectedDriver.id && ACTIVE_RIDE_STATUSES_FOR_ROUTE.includes(r.status));
    if (driverRides.length === 0) return null;
    const priority = { in_progress: 3, arrived: 2, accepted: 1 };
    return driverRides.reduce((best, ride) => (
      !best || (priority[ride.status] || 0) > (priority[best.status] || 0) ? ride : best
    ), null);
  }, [selectedDriver, rides]);

  // Explizit ausgewählte Fahrt (Klick auf Eintrag in Fahrtenliste). Wird stets
  // aus der aktuellen Rides-Liste neu aufgelöst, damit Status-Updates greifen.
  // Wechselt der Nutzer auf einen anderen Fahrer, gilt die alte Fahrtauswahl
  // nicht mehr — die Cleanup-useEffect synchronisiert dann den State. Auch
  // wenn die Fahrt unassignt ist, gewinnt eine explizite Fahrerauswahl.
  const selectedRide = useMemo(() => {
    if (!selectedRideId) return null;
    const ride = rides.find(r => r.id === selectedRideId);
    if (!ride) return null;
    // "Eigentümer" der Fahrt im Sinne der Fahrerzuordnung: bei
    // dispatcher_pending ist das der currentOfferDriverId. Damit ist die
    // Fahrtauswahl auch dann gültig, wenn nur ein Angebots-Fahrer (noch
    // nicht akzeptiert) fokussiert ist.
    const rideOwnerDriverId = ride.driverId || ride.currentOfferDriverId || null;
    if (selectedDriverId && rideOwnerDriverId !== selectedDriverId) return null;
    return ride;
  }, [selectedRideId, rides, selectedDriverId]);

  const selectedRidePickup = useMemo(() => getRidePickupPosition(selectedRide), [selectedRide]);
  const selectedRideDestination = useMemo(() => getRideDestinationPosition(selectedRide), [selectedRide]);
  const selectedRideDriver = useMemo(() => {
    const driverId = selectedRide?.driverId || selectedRide?.currentOfferDriverId;
    if (!driverId) return null;
    return enrichedDriverById[driverId] || null;
  }, [selectedRide, enrichedDriverById]);
  const selectedRideDriverPosition = selectedRideDriver?.position || null;
  const isUnassignedSelectedRide = !!(
    selectedRide
    && !selectedRide.driverId
    && UNASSIGNED_RIDE_STATUSES_FOR_ROUTE.includes(selectedRide.status)
  );
  const isActiveSelectedRide = !!(
    selectedRide
    && ACTIVE_RIDE_STATUSES_FOR_ROUTE.includes(selectedRide.status)
  );
  const isDispatcherPendingSelectedRide = !!(
    selectedRide && selectedRide.status === 'dispatcher_pending'
  );
  // pre_accepted: Vorbestellung, ein Fahrer hat schon zugesagt, aber die
  // Fahrt findet erst später statt. Wir behandeln sie wie eine unassignte
  // Fahrt — nur Kundenstrecke, keine aktuelle Anfahrt vom Fahrer.
  const isPreAcceptedSelectedRide = !!(
    selectedRide && selectedRide.status === 'pre_accepted'
  );
  const showSelectedRide = !!(
    selectedRide
    && (isActiveSelectedRide || isUnassignedSelectedRide || isDispatcherPendingSelectedRide || isPreAcceptedSelectedRide)
    && selectedRidePickup
  );

  // Pickup/Destination der ausgewählten Fahrt in die fitBounds-Extras spiegeln.
  useEffect(() => {
    const extras = [];
    if (showSelectedRide) {
      if (selectedRidePickup) extras.push(selectedRidePickup);
      if (selectedRideDestination) extras.push(selectedRideDestination);
    }
    extraBoundsPointsRef.current = extras;
  }, [
    showSelectedRide,
    selectedRidePickup?.lat,
    selectedRidePickup?.lng,
    selectedRideDestination?.lat,
    selectedRideDestination?.lng,
  ]);

  // Bei Auswahl einer Fahrt initial einmal die Map auf alle relevanten
  // Punkte einpassen (Fahrer + Abhol- + Zielort).
  useEffect(() => {
    if (!map || !showSelectedRide) return;
    applyFitBounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    showSelectedRide,
    selectedRideId,
    selectedRidePickup?.lat,
    selectedRidePickup?.lng,
    selectedRideDestination?.lat,
    selectedRideDestination?.lng,
  ]);

  // Route der ausgewählten aktiven Fahrt berechnen.
  // - accepted: Fahrer → Abholort (orange) + Abholort → Ziel (blau)
  // - arrived:  Abholort → Ziel (blau)
  // - in_progress: Fahrer → Ziel (blau)
  useEffect(() => {
    if (!isLoaded || !showSelectedRide) {
      setRouteLegs([]);
      lastFetchedPhaseKeyRef.current = null;
      return undefined;
    }
    if (!window.google?.maps?.geometry?.encoding?.decodePath) {
      setRouteLegs([]);
      return undefined;
    }

    // Pro Phase (Fahrt-ID + Status) wird die Strecke nur einmal vom
    // DirectionsService geholt. Folgende Lat/Lng-Updates des Fahrers
    // triggern den Effekt zwar erneut, das Phasen-Gate verhindert aber
    // einen weiteren API-Call — der Fahrer-Marker bewegt sich visuell
    // entlang der bereits gezeichneten Polyline.
    const pickupKey = selectedRidePickup ? `${selectedRidePickup.lat},${selectedRidePickup.lng}` : 'no-pickup';
    const destinationKey = selectedRideDestination ? `${selectedRideDestination.lat},${selectedRideDestination.lng}` : 'no-destination';
    // Routing-Präferenz mit ins Phasen-Gate aufnehmen, damit eine Änderung des
    // globalen Defaults (kürzeste/Vermeidungen/Verkehr) einen Neu-Abruf auslöst.
    const routePrefKey = `${settings.routeShortest ? 's' : 'f'}${settings.routeAvoidHighways ? 'H' : ''}${settings.routeAvoidTolls ? 'T' : ''}${settings.routeAvoidFerries ? 'F' : ''}${settings.trafficOptimal ? 'o' : ''}`;
    const phaseKey = `${selectedRide.id}-${selectedRide.status}-${pickupKey}-${destinationKey}-${routePrefKey}`;
    if (lastFetchedPhaseKeyRef.current === phaseKey) {
      return undefined;
    }

    const status = selectedRide.status;
    let origin = null;
    let destination = null;
    const waypoints = [];
    let legStyles = [];

    if (status === 'accepted' && selectedRideDriverPosition && selectedRidePickup && selectedRideDestination) {
      origin = selectedRideDriverPosition;
      destination = selectedRideDestination;
      waypoints.push({ location: selectedRidePickup, stopover: true });
      // Anfahrt zum Kunden: gestrichelt; Zielfahrt: solide (dünner und unter
      // der Anfahrt, damit überlappende Abschnitte beide sichtbar bleiben).
      legStyles = [
        { color: ROUTE_LEG_COLORS.to_pickup, dashed: true },
        { color: ROUTE_LEG_COLORS.to_destination, dashed: false },
      ];
    } else if (status === 'accepted' && selectedRideDriverPosition && selectedRidePickup) {
      origin = selectedRideDriverPosition;
      destination = selectedRidePickup;
      legStyles = [{ color: ROUTE_LEG_COLORS.to_pickup, dashed: true }];
    } else if (status === 'arrived' && selectedRidePickup && selectedRideDestination) {
      origin = selectedRidePickup;
      destination = selectedRideDestination;
      legStyles = [{ color: ROUTE_LEG_COLORS.to_destination, dashed: false }];
    } else if (status === 'in_progress' && selectedRideDriverPosition && selectedRideDestination) {
      origin = selectedRideDriverPosition;
      destination = selectedRideDestination;
      legStyles = [{ color: ROUTE_LEG_COLORS.to_destination, dashed: false }];
    } else if (status === 'dispatcher_pending' && selectedRideDriverPosition && selectedRidePickup && selectedRideDestination) {
      // Fahrer wurde angefragt, hat aber noch nicht akzeptiert. Wir zeigen
      // dieselbe Strecken-Hierarchie wie bei "accepted" — Anfahrt vom
      // angebotenen Fahrer zum Abholort (gestrichelt) + anschließende
      // Zielfahrt (solide blau). Die Strichelung signalisiert das Tentative.
      origin = selectedRideDriverPosition;
      destination = selectedRideDestination;
      waypoints.push({ location: selectedRidePickup, stopover: true });
      legStyles = [
        { color: ROUTE_LEG_COLORS.to_pickup, dashed: true },
        { color: ROUTE_LEG_COLORS.to_destination, dashed: false },
      ];
    } else if (status === 'dispatcher_pending' && selectedRideDriverPosition && selectedRidePickup) {
      origin = selectedRideDriverPosition;
      destination = selectedRidePickup;
      legStyles = [{ color: ROUTE_LEG_COLORS.to_pickup, dashed: true }];
    } else if (
      (
        isUnassignedSelectedRide
        || (status === 'dispatcher_pending' && !selectedRideDriverPosition)
        || status === 'pre_accepted'
      )
      && selectedRidePickup
      && selectedRideDestination
    ) {
      // Unassignte Fahrt (Sofort/Vorbestellung ohne Fahrer),
      // dispatcher_pending ohne bekannte Fahrer-Position oder
      // pre_accepted (Fahrer hat bereits zugesagt, fährt aber später):
      // nur die Kundenstrecke (Abhol → Ziel), solide blau.
      origin = selectedRidePickup;
      destination = selectedRideDestination;
      legStyles = [{ color: ROUTE_LEG_COLORS.to_destination, dashed: false }];
    }

    if (!origin || !destination) {
      setRouteLegs([]);
      return undefined;
    }

    // Phasen-Ref erst jetzt setzen — falls Driver-Position beim ersten
    // Lauf noch fehlt, soll der Effekt es bei der nächsten Auflösung
    // erneut versuchen statt das Gate vorzeitig zu setzen.
    lastFetchedPhaseKeyRef.current = phaseKey;

    let cancelled = false;
    const fallbackStyle = { color: ROUTE_LEG_COLORS.to_destination, dashed: false };

    // Fallback: einfache Luftlinien-Polylinien, falls die Routes API nicht
    // erreichbar ist oder kein Ergebnis liefert.
    const drawFallback = () => {
      const fallback = [];
      if (status === 'accepted' && selectedRideDestination) {
        fallback.push({ path: [origin, selectedRidePickup], ...(legStyles[0] || fallbackStyle) });
        fallback.push({ path: [selectedRidePickup, destination], ...(legStyles[1] || fallbackStyle) });
      } else {
        fallback.push({ path: [origin, destination], ...(legStyles[0] || fallbackStyle) });
      }
      setRouteLegs(fallback);
    };

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      drawFallback();
      return () => { cancelled = true; };
    }

    // Routes API (computeRoutes) MIT denselben Parametern wie die Fahrer-App
    // (RoutesService): so zeigt die LiveMap dieselbe Route wie der Fahrer —
    // inkl. globalem Routing-Default (kürzeste/Vermeidungen/Verkehr) aus
    // appSettings/general. Hinweis: Der PER-FAHRER-Override (lokal auf dem
    // Gerät) ist hier nicht verfügbar und kann nicht nachgebildet werden.
    const toWaypoint = (p) => ({
      location: { latLng: { latitude: p.lat, longitude: p.lng } },
    });

    const body = {
      origin: toWaypoint(origin),
      destination: toWaypoint(destination),
      travelMode: 'DRIVE',
      routingPreference: settings.trafficOptimal ? 'TRAFFIC_AWARE_OPTIMAL' : 'TRAFFIC_AWARE',
      polylineQuality: 'OVERVIEW',
    };
    if (waypoints.length > 0) {
      body.intermediates = waypoints.map(w => toWaypoint(w.location));
    }
    if (settings.routeAvoidHighways || settings.routeAvoidTolls || settings.routeAvoidFerries) {
      body.routeModifiers = {
        ...(settings.routeAvoidHighways ? { avoidHighways: true } : {}),
        ...(settings.routeAvoidTolls ? { avoidTolls: true } : {}),
        ...(settings.routeAvoidFerries ? { avoidFerries: true } : {}),
      };
    }
    if (settings.routeShortest) {
      body.requestedReferenceRoutes = ['SHORTER_DISTANCE'];
    }

    const fieldMaskParts = ['routes.legs.polyline.encodedPolyline'];
    if (settings.routeShortest) fieldMaskParts.push('routes.routeLabels');

    fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': fieldMaskParts.join(','),
      },
      body: JSON.stringify(body),
    })
      .then(res => (res.ok ? res.json() : Promise.reject(new Error(`Routes API HTTP ${res.status}`))))
      .then(data => {
        if (cancelled) return;
        const routes = data?.routes || [];
        if (routes.length === 0) { drawFallback(); return; }

        // Bei „kürzeste Strecke" die Route mit Label SHORTER_DISTANCE wählen
        // (Routes API liefert Default- UND Referenzroute), sonst die erste.
        let route = routes[0];
        if (settings.routeShortest) {
          const shorter = routes.find(
            r => Array.isArray(r.routeLabels) && r.routeLabels.includes('SHORTER_DISTANCE')
          );
          if (shorter) route = shorter;
        }

        const decodePath = window.google.maps.geometry.encoding.decodePath;
        const legs = (route.legs || [])
          .map((leg, index) => ({
            path: leg.polyline?.encodedPolyline ? decodePath(leg.polyline.encodedPolyline) : [],
            ...(legStyles[index] || fallbackStyle),
          }))
          .filter(l => l.path.length > 0);

        if (legs.length === 0) { drawFallback(); return; }
        setRouteLegs(legs);
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('Routes API Fehler, Fallback auf Luftlinie:', err);
        drawFallback();
      });

    return () => {
      cancelled = true;
    };
  }, [
    isLoaded,
    showSelectedRide,
    selectedRide?.status,
    selectedRideDriverPosition?.lat,
    selectedRideDriverPosition?.lng,
    selectedRidePickup?.lat,
    selectedRidePickup?.lng,
    selectedRideDestination?.lat,
    selectedRideDestination?.lng,
    settings.routeShortest,
    settings.routeAvoidHighways,
    settings.routeAvoidTolls,
    settings.routeAvoidFerries,
    settings.trafficOptimal,
  ]);

  // Polylines synchron mit routeLegs zeichnen/entfernen. Alte Linien werden
  // bei jedem Update zuerst mit setMap(null) abgeräumt — auch dann, wenn
  // routeLegs leer wird (etwa beim Schließen der Fahrer-Infokarte).
  useEffect(() => {
    if (!map || !window.google?.maps?.Polyline) {
      routePolylinesRef.current.forEach(line => line.setMap(null));
      routePolylinesRef.current = [];
      return undefined;
    }

    routePolylinesRef.current.forEach(line => line.setMap(null));
    routePolylinesRef.current = [];

    if (!showSelectedRide || routeLegs.length === 0) {
      return undefined;
    }

    const polylines = routeLegs
      .filter(leg => leg.path && leg.path.length > 1)
      .map((leg, index) => {
        if (leg.dashed) {
          // Gestrichelte Anfahrt: eigentliche Linie unsichtbar, Striche
          // werden über wiederholte Icon-Symbole gezeichnet — liegt über
          // der Zielfahrt-Polyline, damit überlappende Abschnitte beide
          // erkennbar bleiben.
          return new window.google.maps.Polyline({
            path: leg.path,
            map,
            strokeOpacity: 0,
            zIndex: 102 + index,
            icons: [{
              icon: {
                path: 'M 0,-1 0,1',
                strokeColor: leg.color,
                strokeOpacity: 1,
                scale: 4,
              },
              offset: '0',
              repeat: '18px',
            }],
          });
        }
        return new window.google.maps.Polyline({
          path: leg.path,
          map,
          strokeColor: leg.color,
          strokeOpacity: 0.9,
          strokeWeight: 4,
          zIndex: 100 + index,
        });
      });

    routePolylinesRef.current = polylines;

    return () => {
      polylines.forEach(line => line.setMap(null));
    };
  }, [map, showSelectedRide, routeLegs]);

  // Wenn die ausgewählte Fahrt nicht mehr darstellbar ist (Status-Wechsel
  // außerhalb der unterstützten Stati, gelöscht, Fahrerwechsel), Auswahl
  // wieder aufheben.
  useEffect(() => {
    if (!selectedRideId) return;
    if (!selectedRide) {
      setSelectedRideId(null);
      return;
    }
    const isValid =
      ACTIVE_RIDE_STATUSES_FOR_ROUTE.includes(selectedRide.status)
      || selectedRide.status === 'dispatcher_pending'
      || selectedRide.status === 'pre_accepted'
      || (!selectedRide.driverId && UNASSIGNED_RIDE_STATUSES_FOR_ROUTE.includes(selectedRide.status));
    if (!isValid) {
      setSelectedRideId(null);
    }
  }, [selectedRideId, selectedRide]);

  const selectedSectorGroup = useMemo(() => {
    if (!selectedSectorId) return null;
    // Map-InfoWindow verwendet die ungefilterte Sektor-Sicht, damit Auswahl
    // nicht durch Listen-Filter ausgeblendet wird.
    return sectorDriverGroupsAll.find(group => group.sector.id === selectedSectorId) || null;
  }, [selectedSectorId, sectorDriverGroupsAll]);

  // Auswahl zurücksetzen, wenn der Fahrer nicht mehr auf der Karte sichtbar ist
  // (durch Filter, Statuswechsel auf offline oder Verlust der Position).
  useEffect(() => {
    if (!selectedDriverId) return;
    const isVisible = driversWithLocation.some(d => d.id === selectedDriverId);
    if (!isVisible) setSelectedDriverId(null);
  }, [selectedDriverId, driversWithLocation]);

  // Fahrtauswahl mit Fahrerauswahl koppeln — aber nur, wenn der Klick die
  // Fahrt im "driver"-Modus aktiviert hat (also der Fahrer parallel
  // fokussiert wurde). Im "unassigned"-Modus ist `selectedDriverId`
  // ohnehin null und die Fahrtauswahl soll davon unabhängig bestehen.
  useEffect(() => {
    if (!selectedRideId) {
      rideFocusModeRef.current = null;
      return;
    }
    if (selectedDriverId) return;
    if (rideFocusModeRef.current === 'driver') {
      setSelectedRideId(null);
    }
  }, [selectedDriverId, selectedRideId]);

  // Fahrer auf Karte fokussieren
  const focusDriver = useCallback((driver) => {
    if (!map || !driver.position) return;
    map.setZoom(16);
    panToCenteredInUsableArea(driver.position);
    setSelectedDriverId(driver.id);
  }, [map, panToCenteredInUsableArea]);

  const focusSector = useCallback((sector) => {
    if (!map || !sector?.polygonPath?.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    sector.polygonPath.forEach(point => bounds.extend(point));
    map.fitBounds(bounds, { ...getCurrentFitPadding(), top: 60, right: 380, bottom: 40 });
    setSelectedSectorId(sector.id);
    setSelectedDriverId(null);
  }, [map, getCurrentFitPadding]);

  // Karte folgt dem ausgewählten Fahrer bei Positions-Updates (nur Pan, kein Zoom).
  // Wenn zusätzlich eine Fahrt ausgewählt ist, übernimmt der Auto-Fit-Mechanismus
  // (inkl. Abhol-/Zielort) — wir würden sonst den Fahrer mittig halten und die
  // Streckenmarker aus dem Bild schieben.
  const selectedDriverPosition = selectedDriver?.position || null;
  useEffect(() => {
    if (!map || !selectedDriverPosition) return;
    if (showSelectedRide) return;
    panToCenteredInUsableArea(selectedDriverPosition);
  }, [map, selectedDriverPosition, panToCenteredInUsableArea, showSelectedRide]);

  useEffect(() => {
    if (!map || !hasInitialFitRef.current) return;
    if (selectedDriverPosition && !showSelectedRide) {
      panToCenteredInUsableArea(selectedDriverPosition);
      return;
    }
    applyFitBounds();
  }, [map, ridesPanelOpen, selectedDriverPosition, panToCenteredInUsableArea, applyFitBounds, showSelectedRide]);

  // Implizites Following bei nur einem sichtbaren Fahrer: Auch ohne explizite
  // Auswahl soll der Marker mitwandern, statt alle 3 s ruckartig vom Auto-Fit
  // zurückgezogen zu werden. Wenn ein Fahrer aktiv ausgewählt ist, gewinnt der
  // obige Follow-Effect und dieser hier bleibt neutral.
  const soloDriver = driversWithLocation.length === 1 ? driversWithLocation[0] : null;
  const soloDriverPosition = soloDriver?.position || null;
  useEffect(() => {
    if (!map || selectedDriverId) return;
    if (!soloDriverPosition) return;
    panToCenteredInUsableArea(soloDriverPosition);
  }, [map, selectedDriverId, soloDriverPosition, panToCenteredInUsableArea]);

  const handleRideMenuOpen = useCallback((event, ride) => {
    event.stopPropagation();
    setRideMenuAnchor(event.currentTarget);
    setRideMenuRide(ride);
  }, []);

  const handleRideMenuClose = useCallback(() => {
    setRideMenuAnchor(null);
    setRideMenuRide(null);
  }, []);

  const clearSelectedRide = useCallback(() => {
    rideFocusModeRef.current = null;
    setSelectedRideId(null);
    setRouteLegs([]);
    setSelectedDriverId(null);
  }, []);

  const handleRideStatusAction = useCallback(async (ride, status, additionalData = {}) => {
    if (!ride) return;
    try {
      await dispatch(updateRideStatus({
        rideId: ride.id,
        status,
        additionalData,
      })).unwrap();
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Fahrtstatus:', error);
    } finally {
      handleRideMenuClose();
    }
  }, [dispatch, handleRideMenuClose]);

  const handleCancelRide = useCallback((ride) => {
    if (['in_progress', 'paused'].includes(ride?.status) &&
        !window.confirm('Diese Fahrt wurde bereits angetreten (Fahrgast an Bord). Wirklich stornieren?')) {
      handleRideMenuClose();
      return;
    }
    handleRideStatusAction(ride, 'cancelled', {
      cancelledBy: currentUser?.uid || null,
      cancelledByRole: currentUser?.role || null,
    });
  }, [currentUser, handleRideStatusAction, handleRideMenuClose]);

  const handleReleaseRide = useCallback((ride) => {
    handleRideStatusAction(ride, 'scheduled', buildDispatcherReleaseData());
  }, [handleRideStatusAction]);

  // InfoWindow-Position: Echtzeit-Position verwenden wenn vorhanden
  const getInfoWindowPosition = useCallback((driver) => {
    if (!driver) return null;
    // Verwende die angereicherte Position (bereits Echtzeit-berücksichtigt)
    return driver.position;
  }, []);

  // Lade-Fehler
  if (loadError) {
    return (
      <Alert severity="error">
        Fehler beim Laden der Google Maps API: {loadError.message}
      </Alert>
    );
  }

  // Ladezustand
  if (!isLoaded || (driversLoading && !drivers.length)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          {!isLoaded ? 'Karte wird geladen...' : 'Fahrerdaten werden geladen...'}
        </Typography>
      </Box>
    );
  }

  // Fehler
  if (driversError) {
    return (
      <Alert severity="error">
        Fehler beim Laden der Fahrerdaten: {typeof driversError === 'object' ? (driversError.message || JSON.stringify(driversError)) : driversError}
      </Alert>
    );
  }

  return (
    <Card elevation={3} className="dashboard-live-map" sx={{ borderRadius: 0, width: '100%', maxWidth: '100%' }}>
      <CardContent sx={{ pb: 0 }}>
        {/* Karte mit Fahrerliste-Overlay */}
        <Box ref={mapContainerRef} sx={{ position: 'relative' }}>
          {/* Eigener Fullscreen-Button */}
          <IconButton
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Vollbild beenden' : 'Vollbild'}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 1,
              bgcolor: 'white',
              boxShadow: 2,
              '&:hover': { bgcolor: 'grey.100' },
            }}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <GoogleMap
            mapContainerStyle={isFullscreen ? mapContainerStyleFullscreen : mapContainerStyle}
            center={defaultCenter}
            zoom={12}
            options={defaultOptions}
            onLoad={onMapLoad}
            mapContainerClassName="google-map-container"
          >
            {/* Sektor-Polygone nur bei aktiver Dispositionsmethode "Nach Sektoren" */}
            {isSectorDispatchActive && activeSectors.map(sector => {
              const color = getSectorColor(sector);
              const isSelected = selectedSectorId === sector.id;
              return (
                <Polygon
                  key={sector.id}
                  paths={sector.polygonPath}
                  options={{
                    fillColor: color,
                    fillOpacity: isSelected ? 0.22 : 0.12,
                    strokeColor: color,
                    strokeOpacity: isSelected ? 0.95 : 0.65,
                    strokeWeight: isSelected ? 3 : 2,
                    clickable: true,
                    zIndex: isSelected ? 2 : 1
                  }}
                  onClick={() => setSelectedSectorId(sector.id)}
                />
              );
            })}

            {/* Fahrer-Marker */}
            {driversWithLocation.map(driver => (
              <AdvancedMarker
                key={driver.id}
                position={driver.position}
                status={driver.derivedStatus}
                label={driver.markerLabel}
                isRealtime={driver.isRealtime}
                onClick={() => setSelectedDriverId(driver.id)}
                title={`${driver.displayName}${driver.isRealtime ? ' (Live)' : ''}`}
              />
            ))}

            {/* Pickup-/Destination-Marker und Routen-Polylines für ausgewählte aktive Fahrt */}
            {showSelectedRide && selectedRidePickup && (
              <AdvancedMarker
                key={`pickup-${selectedRide.id}`}
                position={selectedRidePickup}
                status="pickup"
                title={`Abholort: ${getShortAddress(selectedRide.start || selectedRide.pickup_address)}`}
                zIndex={500}
              />
            )}
            {showSelectedRide && selectedRideDestination && (
              <AdvancedMarker
                key={`destination-${selectedRide.id}`}
                position={selectedRideDestination}
                status="destination"
                title={`Ziel: ${getShortAddress(selectedRide.destination || selectedRide.destination_address)}`}
                zIndex={500}
              />
            )}

            {/* Info-Fenster */}
            {selectedDriver && selectedDriver.position && (
              <InfoWindow
                position={getInfoWindowPosition(selectedDriver)}
                onCloseClick={() => setSelectedDriverId(null)}
                options={{ pixelOffset: new window.google.maps.Size(0, -50) }}
              >
                <Box sx={{ minWidth: 240, maxWidth: 320, p: 0.25 }}>
                  {/* Header: Fahrzeugnummer + Name + Status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                    {selectedDriver.vehicleNumber && (
                      <Box
                        sx={{
                          minWidth: 40,
                          height: 40,
                          px: 0.75,
                          borderRadius: 1.25,
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                          fontSize: '1rem',
                          flexShrink: 0,
                          boxShadow: 1,
                        }}
                      >
                        {selectedDriver.vehicleNumber}
                      </Box>
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {selectedDriver.displayName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        <StatusChip
                          label={STATUS_LABELS[selectedDriver.derivedStatus] || selectedDriver.derivedStatus}
                          status={selectedDriver.derivedStatus}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        {selectedDriver.isRealtime && selectedDriver.derivedStatus !== 'offline' && (
                          <Tooltip
                            arrow
                            title={
                              <Box sx={{ lineHeight: 1.4 }}>
                                <Box>{formatLocationAge(selectedDriver.lastLocationTimestamp)}</Box>
                                <Box>
                                  App: {selectedDriver.appVersion ? `v${selectedDriver.appVersion}` : 'unbekannt'}
                                  {selectedDriver.appPlatform ? ` (${selectedDriver.appPlatform})` : ''}
                                </Box>
                                <Box>
                                  App-Status: {formatAppForegroundState(
                                    selectedDriver.appInForeground,
                                    selectedDriver.appForegroundSince,
                                    selectedDriver.appBackgroundSince
                                  )}
                                </Box>
                                <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.24)' }}>
                                  Standort: {formatPermissionStatus(selectedDriver.locationPermissionStatus)}
                                </Box>
                                <Box>
                                  Immer zulassen: {formatYesNoUnknown(selectedDriver.locationPermissionAllowsBackground)}
                                </Box>
                                <Box>
                                  Vordergrund erlaubt: {formatYesNoUnknown(selectedDriver.locationPermissionAllowsForeground)}
                                </Box>
                                <Box>
                                  Standortdienste: {formatYesNoUnknown(selectedDriver.locationServiceEnabled)}
                                </Box>
                                <Box>
                                  Geprüft: {formatPermissionCheckedAt(selectedDriver.locationPermissionCheckedAt)}
                                </Box>
                                <Box>
                                  Quelle: {formatPermissionSource(selectedDriver.locationPermissionSource)}
                                </Box>
                                {selectedDriver.locationPermissionCheckError && (
                                  <Box sx={{ color: 'warning.light', mt: 0.5 }}>
                                    Fehler: {selectedDriver.locationPermissionCheckError}
                                  </Box>
                                )}
                                <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.24)' }}>
                                  Akku-Optimierung: {formatBatteryOptimizationStatus(
                                    selectedDriver.batteryOptimizationIgnored,
                                    selectedDriver.batteryOptimizationStatus
                                  )}
                                </Box>
                                <Box>
                                  Akku geprüft: {formatPermissionCheckedAt(selectedDriver.batteryOptimizationCheckedAt)}
                                </Box>
                                <Box>
                                  Akku-Quelle: {formatBatteryOptimizationSource(selectedDriver.batteryOptimizationSource)}
                                </Box>
                                {selectedDriver.batteryOptimizationRequestResult && (
                                  <Box>
                                    Letzte Anfrage: {selectedDriver.batteryOptimizationRequestResult}
                                  </Box>
                                )}
                                {selectedDriver.batteryOptimizationCheckError && (
                                  <Box sx={{ color: 'warning.light', mt: 0.5 }}>
                                    Akku-Fehler: {selectedDriver.batteryOptimizationCheckError}
                                  </Box>
                                )}
                              </Box>
                            }
                          >
                            <Chip
                              label="Live"
                              size="small"
                              color="info"
                              sx={{ height: 20, fontSize: '0.7rem', cursor: 'help' }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  {/* Info-Zeilen mit Icons */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <StarIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                      <Typography variant="body2">
                        {selectedDriver.rating || 'Keine'}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          ({selectedDriver.ratingCount || 0})
                        </Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <TaxiIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      <Typography variant="body2" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selectedDriver.vehicleDescription || 'Kein Fahrzeug zugewiesen'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <AccessTimeIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {calculateOnlineTime(selectedDriver)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Aktuelle Fahrt */}
                  {selectedDriverActiveRide && (() => {
                    const isRouteVisible = selectedRideId === selectedDriverActiveRide.id;
                    return (
                    <Box
                      onClick={() => {
                        if (isRouteVisible) {
                          rideFocusModeRef.current = null;
                          setSelectedRideId(null);
                        } else {
                          rideFocusModeRef.current = 'driver';
                          setSelectedRideId(selectedDriverActiveRide.id);
                        }
                      }}
                      title={isRouteVisible ? 'Strecke ausblenden' : 'Strecke auf der Karte anzeigen'}
                      sx={{
                        mt: 1.25,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: isRouteVisible ? 'action.selected' : 'action.hover',
                        border: '1px solid',
                        borderColor: isRouteVisible ? 'primary.main' : 'divider',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s, border-color 0.15s',
                        '&:hover': {
                          bgcolor: 'action.selected',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mb: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                          Aktuelle Fahrt
                        </Typography>
                        <Chip
                          label={RIDE_STATUS_LABELS[selectedDriverActiveRide.status] || selectedDriverActiveRide.status}
                          size="small"
                          color={getRideChipColor(selectedDriverActiveRide.status)}
                          sx={{ height: 18, fontSize: '0.64rem' }}
                        />
                      </Box>

                      {/* Von → Nach als Mini-Timeline */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, position: 'relative' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
                          <LocationOnIcon sx={{ fontSize: '1rem', color: 'success.main', mt: '2px', flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={getShortAddress(selectedDriverActiveRide.start || selectedDriverActiveRide.pickup_address)}
                          >
                            {getShortAddress(selectedDriverActiveRide.start || selectedDriverActiveRide.pickup_address)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
                          <FlagIcon sx={{ fontSize: '1rem', color: 'error.main', mt: '2px', flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={getShortAddress(selectedDriverActiveRide.destination || selectedDriverActiveRide.destination_address)}
                          >
                            {getShortAddress(selectedDriverActiveRide.destination || selectedDriverActiveRide.destination_address)}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, pt: 0.75, borderTop: '1px dashed', borderColor: 'divider', minWidth: 0 }}>
                        <PersonIcon sx={{ fontSize: '1rem', color: 'text.secondary', flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedDriverActiveRide.customerName
                            || selectedDriverActiveRide.customer?.name
                            || selectedDriverActiveRide.medicalTransportSnapshot?.patientName
                            || 'Unbekannter Kunde'}
                        </Typography>
                      </Box>
                    </Box>
                    );
                  })()}

                  <Button
                    variant="outlined"
                    size="small"
                    component={Link}
                    to={`/fahrer/${selectedDriver.id}`}
                    sx={{ mt: 1.25 }}
                    fullWidth
                  >
                    Profil anzeigen
                  </Button>
                </Box>
              </InfoWindow>
            )}

            {isSectorDispatchActive && selectedSectorGroup?.sector?.centerPoint && (
              <InfoWindow
                position={selectedSectorGroup.sector.centerPoint}
                onCloseClick={() => setSelectedSectorId(null)}
              >
                <Box sx={{ minWidth: 220, maxWidth: 300 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {selectedSectorGroup.sector.code ? `${selectedSectorGroup.sector.code} · ` : ''}
                    {selectedSectorGroup.sector.name || 'Sektor'}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Fahrer im Sektor: {selectedSectorGroup.drivers.length}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    Verfügbar: {selectedSectorGroup.availableCount}
                  </Typography>
                  {selectedSectorGroup.drivers.slice(0, 5).map(driver => (
                    <Typography key={driver.id} variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      {driver.sectorQueuePosition ? `${driver.sectorQueuePosition}. ` : ''}
                      {driver.displayName}
                    </Typography>
                  ))}
                </Box>
              </InfoWindow>
            )}
          </GoogleMap>

          {/* Listen-Overlay (links oben auf der Karte) */}
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              maxHeight: 'calc(100% - 20px)',
              display: 'flex',
              alignItems: 'stretch',
              gap: 0.75,
              maxWidth: 'calc(100% - 20px)',
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                width: 340,
                maxHeight: 'calc(85vh - 20px)',
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 2,
                boxShadow: 3,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {/* Statusfilter-Chips – wirken auf beide Listen (Fahrer und Sektoren) */}
              <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    cursor: 'pointer',
                    bgcolor: filtersExpanded ? 'action.hover' : 'transparent',
                  }}
                  onClick={() => setFiltersExpanded((expanded) => !expanded)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <FilterIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, flexShrink: 0 }}>
                      Filter
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Chip
                        label={`Alle (${statusCounts.all})`}
                        variant={filterStatus === 'all' ? 'filled' : 'outlined'}
                        color="default"
                        onClick={(e) => { e.stopPropagation(); setFilterStatus('all'); }}
                        size="small"
                        sx={filterStatus === 'all' ? { bgcolor: 'grey.700', color: 'common.white' } : undefined}
                      />
                      <StatusChip
                        label={`Verfügbar (${statusCounts.available})`}
                        status="available"
                        variant={filterStatus === 'available' ? 'filled' : 'outlined'}
                        onClick={(e) => { e.stopPropagation(); setFilterStatus('available'); }}
                        size="small"
                      />
                    </Box>
                  </Box>
                  <Tooltip title={filtersExpanded ? 'Filter einklappen' : 'Filter ausklappen'} arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFiltersExpanded((expanded) => !expanded);
                      }}
                    >
                      {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
                <Collapse in={filtersExpanded}>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', px: 1.5, pt: 0.25, pb: 1 }}>
                    {/* Fahrtverlauf: Unterwegs → Wartet → Kunde, visuell als Kette */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.25,
                      px: 0.75,
                      py: 0.25,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 999
                    }}>
                      <StatusChip
                        label={`Unterwegs (${statusCounts.en_route})`}
                        status="en_route"
                        variant={filterStatus === 'en_route' ? 'filled' : 'outlined'}
                        onClick={(e) => { e.stopPropagation(); setFilterStatus('en_route'); }}
                        size="small"
                      />
                      <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      <StatusChip
                        label={`Wartet (${statusCounts.waiting})`}
                        status="waiting"
                        variant={filterStatus === 'waiting' ? 'filled' : 'outlined'}
                        onClick={(e) => { e.stopPropagation(); setFilterStatus('waiting'); }}
                        size="small"
                      />
                      <ChevronRightIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      <StatusChip
                        label={`Kunde (${statusCounts.with_customer})`}
                        status="with_customer"
                        variant={filterStatus === 'with_customer' ? 'filled' : 'outlined'}
                        onClick={(e) => { e.stopPropagation(); setFilterStatus('with_customer'); }}
                        size="small"
                      />
                    </Box>
                    <StatusChip
                      label={`Pause (${statusCounts.paused})`}
                      status="paused"
                      variant={filterStatus === 'paused' ? 'filled' : 'outlined'}
                      onClick={(e) => { e.stopPropagation(); setFilterStatus('paused'); }}
                      size="small"
                    />
                    <StatusChip
                      label={`Offline (${statusCounts.offline})`}
                      status="offline"
                      variant={filterStatus === 'offline' ? 'filled' : 'outlined'}
                      onClick={(e) => { e.stopPropagation(); setFilterStatus('offline'); }}
                      size="small"
                    />
                  </Box>
                  {/* Suchfeld – wirkt auf beide Listen */}
                  <Box sx={{ px: 1.5, pb: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Fahrer suchen..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white',
                          fontSize: '0.85rem',
                        }
                      }}
                    />
                  </Box>
                </Collapse>
              </Box>

            {/* Abschnitt-Header: Nach Fahrer (immer sichtbar) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 1.5,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                bgcolor: expandedSection === 'drivers' ? 'action.hover' : 'transparent',
              }}
              onClick={() => setExpandedSection(expandedSection === 'drivers' ? null : 'drivers')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DriversIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Nach Fahrer ({filteredDrivers.length})
                </Typography>
              </Box>
              <IconButton size="small">
                {expandedSection === 'drivers' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse
              in={expandedSection === 'drivers'}
              sx={{
                flex: expandedSection === 'drivers' ? '1 1 auto' : '0 0 auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                '& .MuiCollapse-wrapper': {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                },
                '& .MuiCollapse-wrapperInner': {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                },
              }}
            >
              {/* Fahrerliste */}
              <Box
                sx={{
                  overflowY: 'auto',
                  flex: 1,
                  minHeight: 0,
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'grey.400',
                    borderRadius: 3,
                  },
                }}
              >
                {filteredDrivers.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Keine Fahrer gefunden
                    </Typography>
                  </Box>
                ) : (
                  filteredDrivers.map(driver => (
                    <Box
                      key={driver.id}
                      onClick={() => focusDriver(driver)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        cursor: driver.hasLocation ? 'pointer' : 'default',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        opacity: driver.hasLocation ? 1 : 0.5,
                        bgcolor: selectedDriverId === driver.id ? 'action.selected' : 'transparent',
                        '&:hover': {
                          bgcolor: driver.hasLocation ? 'action.hover' : 'transparent',
                        },
                        transition: 'background-color 0.15s',
                      }}
                    >
                      {/* Status-Punkt */}
                      <StatusDot status={driver.derivedStatus} />

                      {/* Fahrer-Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {driver.derivedStatus !== 'offline' && (
                            <VehicleNumberBadge value={driver.vehicleNumber} />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              minWidth: 0,
                              fontWeight: 600,
                              fontSize: '0.85rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {driver.displayName}
                          </Typography>
                          {driver.isRealtime && driver.derivedStatus !== 'offline' && (
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: getLocationAgeColor(driver.lastLocationTimestamp, locationAgeNowMs),
                                flexShrink: 0,
                              }}
                              title={`Live-Position - ${formatLocationAge(driver.lastLocationTimestamp)}`}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {driver.derivedStatus === 'offline'
                            ? 'Offline'
                            : (driver.vehicleDescription || 'Kein Fahrzeug')}
                        </Typography>
                        {driver.derivedStatus !== 'offline' && driver.locationAddress && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {driver.locationAddress}
                          </Typography>
                        )}
                        {isSectorDispatchActive && driver.currentSectorId && (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontWeight: 600,
                            }}
                          >
                            Sektor: {driver.currentSectorCode || sectorById[driver.currentSectorId]?.code || ''}
                            {(driver.currentSectorCode || sectorById[driver.currentSectorId]?.code) ? ' · ' : ''}
                            {driver.currentSectorName || sectorById[driver.currentSectorId]?.name || driver.currentSectorId}
                            {driver.sectorQueuePosition ? ` · Pos. ${driver.sectorQueuePosition}` : ''}
                          </Typography>
                        )}
                        {isSectorDispatchActive && driver.targetSectorId && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontWeight: 600,
                              color: 'info.main',
                            }}
                          >
                            → Zielsektor: {driver.targetSectorCode || sectorById[driver.targetSectorId]?.code || ''}
                            {(driver.targetSectorCode || sectorById[driver.targetSectorId]?.code) ? ' · ' : ''}
                            {driver.targetSectorName || sectorById[driver.targetSectorId]?.name || driver.targetSectorId}
                            {' · unterwegs'}
                          </Typography>
                        )}
                      </Box>

                      {/* Status-Label */}
                      <StatusChip
                        label={STATUS_LABELS[driver.derivedStatus] || driver.derivedStatus}
                        status={driver.derivedStatus}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </Box>
                  ))
                )}
              </Box>
            </Collapse>

            {/* Abschnitt-Header: Nach Sektoren – nur bei aktiver Sektor-Disposition.
                Klick öffnet die Liste und schließt automatisch die Fahrer-Liste. */}
            {isSectorDispatchActive && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 1,
                    borderTop: '1px solid',
                    borderBottom: expandedSection === 'sectors' ? '1px solid' : 'none',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    bgcolor: expandedSection === 'sectors' ? 'action.hover' : 'transparent',
                  }}
                  onClick={() => setExpandedSection(expandedSection === 'sectors' ? null : 'sectors')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MapIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      Nach Sektoren ({activeSectors.length})
                    </Typography>
                  </Box>
                  <IconButton size="small">
                    {expandedSection === 'sectors' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse
                  in={expandedSection === 'sectors'}
                  sx={{
                    flex: expandedSection === 'sectors' ? '1 1 auto' : '0 0 auto',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    '& .MuiCollapse-wrapper': {
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                    },
                    '& .MuiCollapse-wrapperInner': {
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    },
                  }}
                >
                  <Box
                    sx={{
                      overflowY: 'auto',
                      flex: 1,
                      minHeight: 0,
                      '&::-webkit-scrollbar': { width: 6 },
                      '&::-webkit-scrollbar-thumb': {
                        bgcolor: 'grey.400',
                        borderRadius: 3,
                      },
                    }}
                  >
                    {sectorsLoading && activeSectors.length === 0 ? (
                      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                          Sektoren werden geladen...
                        </Typography>
                      </Box>
                    ) : sectorDriverGroups.length === 0 ? (
                      <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Keine aktiven Sektoren mit Polygon gefunden.
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {sectorDriverGroups.map(({ sector, drivers: sectorDrivers, availableCount }) => {
                          return (
                            <Box
                              key={sector.id}
                              onClick={() => focusSector(sector)}
                              sx={{
                                px: 1.5,
                                py: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                cursor: 'pointer',
                                bgcolor: selectedSectorId === sector.id ? 'action.selected' : 'transparent',
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontWeight: 700,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {sector.code ? `${sector.code} · ` : ''}{sector.name || 'Sektor'}
                                </Typography>
                                <Chip
                                  label={`${availableCount}/${sectorDrivers.length}`}
                                  color={availableCount > 0 ? 'success' : 'default'}
                                  size="small"
                                  variant={availableCount > 0 ? 'filled' : 'outlined'}
                                  sx={{ height: 22 }}
                                />
                              </Box>

                              {sectorDrivers.length > 0 ? (
                                <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {sectorDrivers.map(driver => (
                                    <Box
                                      key={driver.id}
                                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}
                                    >
                                      <StatusDot status={driver.derivedStatus} />
                                      {driver.sectorQueuePosition && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ minWidth: 15, textAlign: 'right', flexShrink: 0 }}
                                        >
                                          {driver.sectorQueuePosition}.
                                        </Typography>
                                      )}
                                      <VehicleNumberBadge value={driver.vehicleNumber} compact />
                                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            minWidth: 0,
                                            fontWeight: 600,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          }}
                                        >
                                          {driver.displayName}
                                        </Typography>
                                        {driver.locationAddress && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                              minWidth: 0,
                                              maxWidth: '48%',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              flexShrink: 1,
                                            }}
                                          >
                                            · {driver.locationAddress}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Typography variant="caption" color="text.secondary">
                                        {STATUS_LABELS[driver.derivedStatus] || driver.derivedStatus}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  Kein Fahrer im aktuellen Filter in diesem Sektor
                                </Typography>
                              )}
                            </Box>
                          );
                        })}

                        {/* Fahrer ohne Sektor-Anmeldung */}
                        <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'grey.400', flexShrink: 0 }} />
                            <Typography
                              variant="body2"
                              sx={{ flex: 1, fontWeight: 700, color: 'text.secondary' }}
                            >
                              Ohne Sektor
                            </Typography>
                            <Chip
                              label={driversWithoutSector.length}
                              color="default"
                              size="small"
                              variant="outlined"
                              sx={{ height: 22 }}
                            />
                          </Box>
                          {driversWithoutSector.length > 0 ? (
                            <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {driversWithoutSector.map(driver => (
                                <Box
                                  key={driver.id}
                                  onClick={() => focusDriver(driver)}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    minWidth: 0,
                                    cursor: driver.hasLocation ? 'pointer' : 'default',
                                    opacity: driver.hasLocation ? 1 : 0.6,
                                    '&:hover': { bgcolor: driver.hasLocation ? 'action.hover' : 'transparent' },
                                    px: 0.5,
                                    borderRadius: 0.5,
                                  }}
                                >
                                  <StatusDot status={driver.derivedStatus} />
                                  <VehicleNumberBadge value={driver.vehicleNumber} compact />
                                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        minWidth: 0,
                                        fontWeight: 600,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {driver.displayName}
                                    </Typography>
                                    {driver.locationAddress && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          minWidth: 0,
                                          maxWidth: '48%',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          flexShrink: 1,
                                        }}
                                      >
                                        · {driver.locationAddress}
                                      </Typography>
                                    )}
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {STATUS_LABELS[driver.derivedStatus] || driver.derivedStatus}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Alle Fahrer im aktuellen Filter sind einem Sektor zugeordnet.
                            </Typography>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </Collapse>
              </>
            )}
            </Box>

            <Box sx={{ display: 'flex', maxHeight: 'calc(85vh - 20px)', flexShrink: 0 }}>
              <Box
                onClick={() => setRidesPanelOpen(open => !open)}
                sx={{
                  width: 42,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: 2,
                  boxShadow: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                title={ridesPanelOpen ? 'Fahrtenliste schließen' : 'Fahrtenliste öffnen'}
              >
                <RouteIcon color="primary" fontSize="small" />
                <Chip
                  label={liveMapRides.length}
                  size="small"
                  color="primary"
                  sx={{ height: 22, minWidth: 28, '& .MuiChip-label': { px: 0.75 } }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    fontWeight: 700,
                    letterSpacing: 0,
                  }}
                >
                  Fahrten
                </Typography>
                {ridesPanelOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Box>

              <Collapse in={ridesPanelOpen} orientation="horizontal" timeout="auto">
                <Box
                  sx={{
                    width: 420,
                    maxWidth: 420,
                    height: '100%',
                    maxHeight: 'calc(85vh - 20px)',
                    ml: 0.75,
                    bgcolor: 'rgba(255, 255, 255, 0.97)',
                    borderRadius: 2,
                    boxShadow: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      px: 1.5,
                      py: 1.25,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        Fahrten ({liveMapRides.length})
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Heute + nächste 12h
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Neue Fahrt erstellen"
                      title="Neue Fahrt erstellen"
                      onClick={() => navigate('/fahrten/neu', { state: { from: '/dashboard' } })}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box
                    sx={{
                      overflowY: 'auto',
                      flex: 1,
                      '&::-webkit-scrollbar': { width: 6 },
                      '&::-webkit-scrollbar-thumb': {
                        bgcolor: 'grey.400',
                        borderRadius: 3,
                      },
                    }}
                  >
                    {liveMapRides.length === 0 ? (
                      <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Keine relevanten Fahrten
                        </Typography>
                      </Box>
                    ) : (
                      liveMapRides.map((ride, index) => {
                        const displayStatus = getRideDisplayStatus(ride);
                        const displayDriverId = ride.driverId || ride.currentOfferDriverId || null;
                        const displayDriver = displayDriverId ? enrichedDriverById[displayDriverId] : null;
                        const canFocusDriver = !!displayDriver?.position;
                        const driverName = getRideDriverName(ride, enrichedDriverById);
                        const vehicleNumber = getRideVehicleNumber(ride, enrichedDriverById, vehicleByDriverId);
                        const vehiclePlate = getRideVehiclePlate(ride, enrichedDriverById, vehicleByDriverId);
                        const hasCurrentOffer = !ride.driverId && !!ride.currentOfferDriverId;
                        const showDriverVehicleBadge = !!vehicleNumber && (hasCurrentOffer || !!driverName);
                        const fallbackVehicleLabel = !vehicleNumber && vehiclePlate ? vehiclePlate : null;
                        const isPreOrder = ride.orderType === 1 || ride.convertedToOpenRide;
                        const isPhoneCustomer = !!ride.isPhoneCustomer;
                        const requestedVehicleType = Number(ride.vehicleType) || 1;
                        const requestedCapabilities = ride.specialCapabilities && Array.isArray(ride.specialCapabilities)
                          ? ride.specialCapabilities
                          : migrateSpecialVehicleType(ride);

                        const isActiveRide = ACTIVE_RIDE_STATUSES_FOR_ROUTE.includes(ride.status);
                        const isDispatcherPending = ride.status === 'dispatcher_pending';
                        const isPreAccepted = ride.status === 'pre_accepted';
                        const hasRidePickup = !!getRidePickupPosition(ride);
                        const isUnassignedDisplayable =
                          !ride.driverId &&
                          UNASSIGNED_RIDE_STATUSES_FOR_ROUTE.includes(ride.status) &&
                          hasRidePickup;
                        const isClickable =
                          (isActiveRide && canFocusDriver)
                          || (isDispatcherPending && hasRidePickup)
                          || (isPreAccepted && hasRidePickup)
                          || isUnassignedDisplayable;
                        const isRideSelected = selectedRideId === ride.id;
                        return (
                          <Box
                            key={ride.id}
                            onClick={() => {
                              if (isRideSelected) {
                                clearSelectedRide();
                                return;
                              }
                              if (isActiveRide && canFocusDriver) {
                                rideFocusModeRef.current = 'ride';
                                setSelectedRideId(ride.id);
                                setSelectedDriverId(null);
                                      } else if (isDispatcherPending && hasRidePickup) {
                                if (canFocusDriver) {
                                  rideFocusModeRef.current = 'ride';
                                  setSelectedRideId(ride.id);
                                  setSelectedDriverId(null);
                                } else {
                                  rideFocusModeRef.current = 'unassigned';
                                  setSelectedRideId(ride.id);
                                  setSelectedDriverId(null);
                                }
                                      } else if (isPreAccepted && hasRidePickup) {
                                // Wie open behandeln: nur Kundenstrecke,
                                // kein Fahrerfokus (Fahrt liegt in der
                                // Zukunft).
                                rideFocusModeRef.current = 'unassigned';
                                setSelectedRideId(ride.id);
                                setSelectedDriverId(null);
                              } else if (isUnassignedDisplayable) {
                                rideFocusModeRef.current = 'unassigned';
                                setSelectedRideId(ride.id);
                                setSelectedDriverId(null);
                              } else if (canFocusDriver) {
                                rideFocusModeRef.current = null;
                                setSelectedRideId(null);
                                focusDriver(displayDriver);
                              }
                            }}
                            sx={{
                              px: 1.5,
                              py: 1.25,
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                      borderLeft: isRideSelected ? '3px solid' : '3px solid transparent',
                                      borderLeftColor: isRideSelected ? ROUTE_LEG_COLORS.to_destination : 'transparent',
                                      bgcolor: isRideSelected
                                        ? SELECTED_RIDE_BACKGROUND
                                        : (index % 2 === 1 ? 'rgba(25, 118, 210, 0.08)' : 'transparent'),
                                      cursor: isClickable || isRideSelected ? 'pointer' : 'default',
                                      '&:hover': {
                                        bgcolor: (isClickable || isRideSelected)
                                          ? SELECTED_RIDE_HOVER_BACKGROUND
                                          : (index % 2 === 1 ? 'rgba(25, 118, 210, 0.08)' : 'transparent'),
                                      },
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.75, mb: 0.75 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
                                    <ConvertedPreorderBadge
                                      badgeContent={ride.convertedToOpenRide ? '!' : 0}
                                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    >
                                      <Chip
                                        icon={isPreOrder ? <EventIcon fontSize="small" /> : <FlashOnIcon fontSize="small" />}
                                        label={isPreOrder ? 'Vorbestellung' : 'Sofort'}
                                        size="small"
                                        color={isPreOrder ? 'secondary' : 'primary'}
                                        variant="outlined"
                                        sx={{ height: 22, fontSize: '0.7rem' }}
                                      />
                                    </ConvertedPreorderBadge>
                                    <Chip
                                      label={RIDE_STATUS_LABELS[displayStatus] || displayStatus}
                                      size="small"
                                      color={getRideChipColor(displayStatus)}
                                      sx={{ height: 22, fontSize: '0.7rem' }}
                                    />
                                    <Chip
                                      icon={isPhoneCustomer ? <PhoneIcon fontSize="small" /> : <SmartphoneIcon fontSize="small" />}
                                      label={isPhoneCustomer ? 'Telefon' : 'App'}
                                      size="small"
                                      color={isPhoneCustomer ? 'default' : 'success'}
                                      variant="outlined"
                                      sx={{ height: 22, fontSize: '0.7rem' }}
                                    />
                                    {(ride.source === 'ai_telephonist' || ride.createdByRole === 'ai_telephonist') && (
                                      <Chip
                                        label="🤖 KI-Buchung"
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          height: 22,
                                          fontSize: '0.7rem',
                                          borderColor: 'info.main',
                                          color: 'info.dark',
                                          '& .MuiChip-icon': { color: 'info.dark' },
                                        }}
                                      />
                                    )}
                                    <PaymentChip ride={ride} sx={{ height: 22, fontSize: '0.7rem' }} />
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: -0.5, mr: -0.75, flexShrink: 0 }}>
                                    {isRideSelected && (
                                      <Tooltip title="Anzeige beenden" arrow>
                                                <IconButton
                                                  size="small"
                                                  aria-label="Anzeige beenden"
                                                  onClick={(event) => {
                                                    event.stopPropagation();
                                                    clearSelectedRide();
                                                  }}
                                                  sx={{
                                                    color: ROUTE_LEG_COLORS.to_destination,
                                                    '&:hover': {
                                                      bgcolor: SELECTED_RIDE_HOVER_BACKGROUND,
                                                    },
                                                  }}
                                                >
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <IconButton
                                      size="small"
                                      aria-label="Fahrtaktionen"
                                      onClick={(event) => handleRideMenuOpen(event, ride)}
                                      sx={{ flexShrink: 0 }}
                                    >
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>

                                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={getShortAddress(ride.start || ride.pickup_address)}>
                                  Von: {getShortAddress(ride.start || ride.pickup_address)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap title={getShortAddress(ride.destination || ride.destination_address)}>
                                  Nach: {getShortAddress(ride.destination || ride.destination_address)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                                  {getRidePrimaryTimeLabel(ride)}
                                </Typography>
                                {(ride.status === 'scheduled' || ride.status === 'pre_accepted')
                                  && ride.dispatchOpenAt
                                  && ride.pickupServiceAreaStatus === 'outside' && (
                                  <Typography variant="caption" sx={{ display: 'block', color: 'warning.dark', fontWeight: 600 }} noWrap>
                                    {ride.status === 'pre_accepted' ? 'Online-Prüfung' : 'Freigabe'}: {formatRideDateTime(ride.dispatchOpenAt)}
                                    {Number.isFinite(ride.preorderDispatchLeadMinutes)
                                      ? ` (${ride.preorderDispatchLeadMinutes} Min. Vorlauf)`
                                      : ''}
                                    {Number.isFinite(ride.preorderApproachEstimateMinutes)
                                      ? ` · Anfahrt ~${ride.preorderApproachEstimateMinutes} Min.`
                                      : ''}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                                  Kunde: {ride.customerName || ride.customer?.name || ride.medicalTransportSnapshot?.patientName || 'Unbekannt'}
                                </Typography>
                                {ride.notes && (
                                  <Box
                                    title={ride.notes}
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      mt: 0.25,
                                      px: 0.75,
                                      py: 0.125,
                                      maxWidth: '100%',
                                      borderRadius: 0.75,
                                      bgcolor: 'rgba(255, 213, 79, 0.35)',
                                      borderLeft: '3px solid',
                                      borderColor: 'warning.main',
                                    }}
                                  >
                                    <NoteIcon sx={{ fontSize: '0.85rem', color: 'warning.dark', flexShrink: 0 }} />
                                    <Typography
                                      variant="caption"
                                      noWrap
                                      sx={{
                                        fontStyle: 'italic',
                                        fontWeight: 600,
                                        color: 'warning.dark',
                                        minWidth: 0,
                                      }}
                                    >
                                      Notiz: {ride.notes}
                                    </Typography>
                                  </Box>
                                )}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    minWidth: 0,
                                    color: driverName || hasCurrentOffer ? 'text.secondary' : 'warning.dark',
                                  }}
                                >
                                  <Typography variant="caption" component="span" sx={{ flexShrink: 0 }}>
                                    Fahrer:
                                  </Typography>
                                  {hasCurrentOffer && (
                                    <Chip
                                      label="Angefragt"
                                      size="small"
                                      color="warning"
                                      sx={{ height: 18, fontSize: '0.64rem', flexShrink: 0 }}
                                    />
                                  )}
                                  {showDriverVehicleBadge && (
                                    <VehicleNumberBadge value={vehicleNumber} compact />
                                  )}
                                  <Typography variant="caption" component="span" noWrap sx={{ minWidth: 0 }}>
                                    {driverName || (hasCurrentOffer ? 'Unbekannt' : 'Kein Fahrer')}
                                    {fallbackVehicleLabel ? ` · ${fallbackVehicleLabel}` : ''}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                                  {ride.isKrankenfahrt && (
                                    <Chip icon={<MedicalIcon fontSize="small" />} label="Krankenfahrt" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                                  )}
                                  {ride.isRechnungsfahrt && (
                                    <Chip label="Rechnung" size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                                  )}
                                  {(ride.isRecurring || ride.recurringOrderId) && (
                                    <Chip icon={<RecurringIcon fontSize="small" />} label="Dauerauftrag" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                                  )}
                                  {ride.isAnschlusstour && (
                                    <Chip label="AT" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                                  )}
                                  {isAutoAcceptedOpenPreOrder(ride) && (
                                    <Chip label="Auto bestätigt" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                                  )}
                                  {ride.currentOfferDriverId && displayStatus === 'dispatcher_pending' && (
                                    <Chip label="Fahrer angefragt" size="small" color="warning" sx={{ height: 20, fontSize: '0.68rem' }} />
                                  )}
                                  <Chip
                                    label={getVehicleTypeChipLabel(requestedVehicleType)}
                                    size="small"
                                    color="default"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.68rem' }}
                                  />
                                  {requestedCapabilities.map((cap) => (
                                    <Chip
                                      key={cap}
                                      label={getCapabilityLabel(cap)}
                                      size="small"
                                      color="default"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.68rem' }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            </Box>

                          </Box>
                        );
                      })
                    )}
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Box>

          <Menu
            id={rideMenuRide ? `live-map-ride-menu-${rideMenuRide.id}` : undefined}
            anchorEl={rideMenuAnchor}
            open={Boolean(rideMenuAnchor)}
            onClose={handleRideMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem
              component={Link}
              to={rideMenuRide ? `/fahrten/${rideMenuRide.id}` : '#'}
              onClick={handleRideMenuClose}
            >
              Fahrt anzeigen
            </MenuItem>
            {rideMenuRide && !FINAL_RIDE_STATUSES.includes(rideMenuRide.status) && rideMenuRide.status !== 'open' && rideMenuRide.status !== 'scheduled' && (
              <MenuItem
                component={Link}
                to={`/fahrten/${rideMenuRide.id}/bearbeiten`}
                state={{ from: '/dashboard' }}
                onClick={handleRideMenuClose}
              >
                <EditIcon fontSize="small" sx={{ mr: 1 }} />
                Bearbeiten
              </MenuItem>
            )}
            {rideMenuRide && ASSIGNABLE_RIDE_STATUSES.includes(rideMenuRide.status) && (
              <MenuItem
                component={Link}
                to={`/fahrten/${rideMenuRide.id}/zuweisen`}
                state={{ from: '/dashboard' }}
                onClick={handleRideMenuClose}
              >
                <TaxiIcon fontSize="small" sx={{ mr: 1 }} />
                {rideMenuRide.driverId ? 'Fahrer neu zuweisen' : 'Fahrer zuweisen'}
              </MenuItem>
            )}
            {rideMenuRide && ['accepted', 'arrived'].includes(rideMenuRide.status) && (
              <MenuItem onClick={() => handleRideStatusAction(rideMenuRide, 'in_progress')}>
                <StartIcon fontSize="small" sx={{ mr: 1 }} />
                Fahrgast zugestiegen
              </MenuItem>
            )}
            {rideMenuRide && ['arrived', 'in_progress'].includes(rideMenuRide.status) && (
              <MenuItem onClick={() => handleRideStatusAction(rideMenuRide, 'completed')}>
                <EndIcon fontSize="small" sx={{ mr: 1 }} />
                Fahrt abschließen
              </MenuItem>
            )}
            {rideMenuRide && rideMenuRide.status === 'pre_accepted' && (
              <MenuItem onClick={() => handleReleaseRide(rideMenuRide)}>
                <SwapHorizIcon fontSize="small" sx={{ mr: 1 }} />
                Fahrt zurücknehmen
              </MenuItem>
            )}
            {rideMenuRide && (rideMenuRide.status === 'scheduled' || rideMenuRide.status === 'pre_accepted') && (!rideMenuRide.gegenvorschlagStatus || rideMenuRide.gegenvorschlagStatus === 'expired') && (
              <MenuItem
                component={Link}
                to={`/fahrten/${rideMenuRide.id}`}
                onClick={handleRideMenuClose}
              >
                <SwapHorizIcon fontSize="small" sx={{ mr: 1 }} />
                Gegenvorschlag senden
              </MenuItem>
            )}
            {rideMenuRide && DUPLICATABLE_RIDE_STATUSES.includes(rideMenuRide.status) && (
              <MenuItem
                onClick={() => {
                  const duplicateRide = rideMenuRide;
                  handleRideMenuClose();
                  navigate('/fahrten/neu', { state: { duplicateRide, from: '/dashboard' } });
                }}
              >
                <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
                {['completed', 'cancelled', 'cancelled_by_driver'].includes(rideMenuRide.status)
                  ? 'Fahrt erneut erstellen'
                  : '2. Taxi anfordern'}
              </MenuItem>
            )}
            {rideMenuRide && CANCELLABLE_RIDE_STATUSES.includes(rideMenuRide.status) && (
              <MenuItem
                onClick={() => handleCancelRide(rideMenuRide)}
                sx={{ color: 'error.main' }}
              >
                <CancelledIcon fontSize="small" sx={{ mr: 1 }} />
                Stornieren
              </MenuItem>
            )}
          </Menu>
        </Box>
      </CardContent>
    </Card>
  );
});

export default LiveMap;
